import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { formatCurrency, formatDate, getPreferredDateInputValue } from '../../../utils/formatters';
import {
  getLedgerAccountById,
  getLedgerAccountTransactions,
  useRecordPayment,
  useGetEarlyRepaymentQuote,
  useRecordEarlyRepayment,
  useListPenaltyReviews,
  useEditPenaltyReviewCharge,
  useApprovePenaltyReview,
  useRejectPenaltyReview,
  useOverridePenaltyReview,
  useCalculatePenalties,
  useRectifyPayment
} from '../../../services/loanService';
import type { LedgerAccount } from '../../../services/schemas/loanServiceSchemas/ledgerAccount';
import type { LedgerTransaction } from '../../../services/schemas/loanServiceSchemas/ledgerTransaction';
import type { LedgerInstallment } from '../../../services/schemas/loanServiceSchemas/ledgerInstallment';
import type { EarlyRepaymentQuote } from '../../../services/schemas/loanServiceSchemas/earlyRepaymentQuote';
import type { PenaltyReview } from '../../../services/schemas/loanServiceSchemas/penaltyReview';
import type { CalculatedPenalty } from '../../../services/schemas/loanServiceSchemas/calculatedPenalty';
import { PenaltyReviewStatus } from '../../../services/schemas/loanServiceSchemas/penaltyReviewStatus';
import DataTable from 'react-data-table-component';
import { Modal as BootstrapModal } from 'bootstrap';
import { Dropdown, Modal, Button } from 'react-bootstrap';
import { reverseCharge as reverseChargeRequest } from '../../../services/chargeControls';
import { createPortal } from 'react-dom';
import LoanEditModal from '../../../components/modals/LoanEditModal';
import './modal.css';
import './collections-actions.css';
import { useForm, FormProvider } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {RepaymentFrequency} from "../../../services/schemas/loanServiceSchemas";
import { useSearchCustomers } from '../../../services/customerService';
import type { Customer } from '../../../services/schemas/customerServiceSchemas/customer';
import { useGetLedgerAccountsByCustomer, downloadSettlementLetter } from '../../../services/loanService';
import LoanApplicationComments from '../../../components/common/LoanApplicationComments';
import CreditAssessmentSummary from '../../../components/common/CreditAssessmentSummary';
import LoanAgreementsCard from '../../../components/common/LoanAgreementsCard';
import DisbursementsCard from '../../../components/common/DisbursementsCard';
import DiscountWaiverModal from './DiscountWaiverModal';
import LoanStatementModal from './LoanStatementModal';
import ReverseDiscountModal from './ReverseDiscountModal';
import ApprovalConditionsCard from '../../../components/common/ApprovalConditionsCard';
import CollectionNotes from '../../../components/common/CollectionNotes';
import type { LoanApplicationStatus } from '../../../services/schemas/loanServiceSchemas/loanApplicationStatus';
import { toLocalDateString } from '../../../utils/localDate';

// Simple UUID v4 generator function
const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// What is still owed on one installment: scheduled principal, interest and fees less what has been paid
/** What is still owed on an instalment: its scheduled amount and the default charges raised against it, less payments. */
const installmentRemaining = (row: LedgerInstallment): number => {
  const scheduled = (row.principalDue || 0) + (row.interestDue || 0) + (row.feesDue || 0);
  const paid = (row.principalPaid || 0) + (row.interestPaid || 0) + (row.feesPaid || 0);
  return Math.max(scheduled - paid, 0) + installmentChargesOutstanding(row);
};

/** Default charges raised against the instalment and not yet paid. */
const installmentChargesOutstanding = (row: LedgerInstallment): number =>
  Math.max((row.penaltiesDue || 0) - (row.penaltiesPaid || 0), 0);

/** The due date agreed in the schedule; a charge can move the date it is payable by, never this one. */
const agreedDueDate = (row: LedgerInstallment): string => row.originalDueDate || row.dueDate || '';

// Add payment form schema
const paymentFormSchema = z.object({
  paymentAmount: z.string()
      .min(1, 'Payment amount is required')
      .refine((val) => !isNaN(Number(val)) && Number(val) > 0, {
        message: 'Payment amount must be greater than 0'
      }),
  valueDate: z.string()
      .min(1, 'Payment date is required')
      .refine((val) => !isNaN(Date.parse(val)), {
        message: 'Please select a valid date'
      }),
  externalRef: z.string().optional(),
  remarks: z.string().optional()
});

type PaymentFormData = z.infer<typeof paymentFormSchema>;

// Add early repayment form schema
const earlyRepaymentFormSchema = z.object({
  valueDate: z.string()
      .min(1, 'Payment date is required')
      .refine((val) => !isNaN(Date.parse(val)), {
        message: 'Please select a valid date'
      }),
  externalRef: z.string().optional(),
  remarks: z.string().optional()
});


















type EarlyRepaymentFormData = z.infer<typeof earlyRepaymentFormSchema>;

type ReallocateFundsFormState = {
  targetCustomerId: string;
  targetAccountId: string;
  amount: string;
  valueDate: string;
  remarks: string;
};


type RectifyLoanFormState = {
  originalTxnId: string;
  targetAccountId: string;
  correctAmount: string;
  correctValueDate: string;
  externalRef: string;
  remarks: string;
};

// Render children into document.body to avoid stacking-context issues
const Portal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (typeof document === 'undefined') return null;
  return createPortal(children, document.body);
};

// Installment Actions component
const InstallmentActions: React.FC<{ installment: LedgerInstallment }> = ({ installment }) => {
  const [show, setShow] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const targetRef = React.useRef<HTMLButtonElement | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);

  const handleEditLoan = () => {
    setShow(false);
    toast.info('Loan restructuring will be available in a future update');
  };

  const close = () => {
    setShow(false);
  };

  const onToggleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setShow((current) => !current);
  };

  useEffect(() => {
    if (!show) {
      return;
    }

    const updatePosition = () => {
      const target = targetRef.current;
      const menu = menuRef.current;

      if (!target || !menu) {
        return;
      }

      const spacing = 8;
      const rect = target.getBoundingClientRect();
      const menuWidth = menu.offsetWidth;
      const menuHeight = menu.offsetHeight;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let left = rect.right - menuWidth;
      if (left < spacing) {
        left = spacing;
      }
      if (left + menuWidth > viewportWidth - spacing) {
        left = viewportWidth - menuWidth - spacing;
      }

      const canOpenDown = rect.bottom + spacing + menuHeight <= viewportHeight - spacing;
      const canOpenUp = rect.top - spacing - menuHeight >= spacing;
      const top = !canOpenDown && canOpenUp
        ? rect.top - menuHeight - spacing
        : Math.min(rect.bottom + spacing, viewportHeight - menuHeight - spacing);

      setMenuStyle({
        position: 'fixed',
        top,
        left,
        zIndex: 99999,
      });
    };

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        targetRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      close();
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [show]);

  return (
    <div className="d-flex align-items-center">
      <button
        ref={targetRef}
        type="button"
        className="btn btn-sm btn-light rounded-pill d-flex align-items-center"
        onClick={onToggleClick}
      >
        <span>Actions</span>
        <i className="fa fa-caret-down ms-2"></i>
      </button>
      {show && typeof document !== 'undefined' && createPortal(
        <div
          ref={menuRef}
          className="loan-book-actions-menu"
          style={menuStyle}
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            className="loan-book-actions-item action-edit"
            onClick={handleEditLoan}
          >
            <i className="fa fa-pencil"></i>
            Edit Ledger
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};

const LoanAccountDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Debug: Log the ID from URL
  useEffect(() => {
    console.log('LoanAccountDetails: Route ID from params:', id);
  }, [id]);
  const [loanAccount, setLoanAccount] = useState<LedgerAccount | null>(null);

  // Opened from a collections list to log a contact: go straight to the follow-up history once the account shows
  useEffect(() => {
    if (!loanAccount?.accountId || window.location.hash !== '#follow-up') return;
    const timer = window.setTimeout(() => {
      document.getElementById('follow-up')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [loanAccount?.accountId]);
  const [paymentHistory, setPaymentHistory] = useState<LedgerTransaction[]>([]);
  const [allTransactions, setAllTransactions] = useState<LedgerTransaction[]>([]);
  const [showAuditTrailModal, setShowAuditTrailModal] = useState(false);
  const [showViewRemarksModal, setShowViewRemarksModal] = useState(false);
  const [viewRemarksTarget, setViewRemarksTarget] = useState<LedgerTransaction | null>(null);
  const [rectifiedOriginalTxnIds, setRectifiedOriginalTxnIds] = useState<string[]>([]);
  const [rectificationDetailsMap, setRectificationDetailsMap] = useState<Record<string, { remarks?: string; externalRef?: string; targetAccountId?: string }>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPayments, setIsLoadingPayments] = useState(true);

  // Payment mutation
  const recordPaymentMutation = useRecordPayment({
    mutation: {
      onSuccess: () => {
        toast.success('Payment processed successfully');
        fetchLoanAccount();
        fetchPaymentHistory();

        // Close modal only after successful response
        const modalElement = document.getElementById('LoanAccountPayment');
        if (modalElement) {
          const modal = BootstrapModal.getInstance(modalElement);
          if (modal) modal.hide();
        }
        setShowPaymentModal(false);
        reset();
        setIsProcessingPayment(false);
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to process payment');
        setIsProcessingPayment(false);
        // Don't close modal on error so user can retry
      }
    }
  });

  // Early repayment quote mutation
  const getEarlyRepaymentQuoteMutation = useGetEarlyRepaymentQuote({
    mutation: {
      onSuccess: (data) => {
        setEarlyRepaymentCalculation(data);
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to calculate early repayment');
      }
    }
  });

  // Early repayment mutation
  const recordEarlyRepaymentMutation = useRecordEarlyRepayment({
    mutation: {
      onSuccess: () => {
        toast.success('Early repayment processed successfully');
        fetchLoanAccount();
        fetchPaymentHistory();

        // Close modal only after successful response
        const modalElement = document.getElementById('LoanAccountEarlyRepayment');
        if (modalElement) {
          const modal = BootstrapModal.getInstance(modalElement);
          if (modal) modal.hide();
        }
        setShowEarlyRepaymentModal(false);
        resetEarlyRepayment();
        setIsProcessingEarlyRepayment(false);
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to process early repayment');
        setIsProcessingEarlyRepayment(false);
        // Don't close modal on error so user can retry
      }
    }
  });

    const [showRejectModal, setShowRejectModal] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedPenalty, setSelectedPenalty] = useState<PenaltyReview | null>(null);
  const [overrideAmount, setOverrideAmount] = useState<number>(0);
  const [overrideAsOfDate, setOverrideAsOfDate] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalDueDate, setApprovalDueDate] = useState('');
  const [overrideDueDate, setOverrideDueDate] = useState('');
  const [isPreparingOverride, setIsPreparingOverride] = useState(false);
  const [isHistoryEdit, setIsHistoryEdit] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);




















  const [showEarlyRepaymentModal, setShowEarlyRepaymentModal] = useState(false);
  const [earlyRepaymentCalculation, setEarlyRepaymentCalculation] = useState<EarlyRepaymentQuote | null>(null);
  const [isCalculatingEarlyRepayment, setIsCalculatingEarlyRepayment] = useState(false);
  const [isProcessingEarlyRepayment, setIsProcessingEarlyRepayment] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isProcessingRectify, setIsProcessingRectify] = useState(false);
  const [isProcessingReallocate, setIsProcessingReallocate] = useState(false);
  const [editingLoan, setEditingLoan] = useState<{
    id: string;
    loanAmount: number;
    interestRate: number;
    dueDate: string;
    commencementDate: string;
    adminFee: number;
    customerName: string;
    accountNumber: string;
  } | null>(null);
  const [selectedPaymentActionRow, setSelectedPaymentActionRow] = useState<LedgerTransaction | null>(null);
  const [currentOperation, setCurrentOperation] = useState<'rectify' | 'reallocate' | null>(null);
    const [reallocateFundsForm, setReallocateFundsForm] = useState<ReallocateFundsFormState>({
    targetCustomerId: '',
    targetAccountId: '',
    amount: '',
    valueDate: toLocalDateString(),
    remarks: ''
  });

    // Typeahead/search state for companies
  const [companyQuery, setCompanyQuery] = useState('');
  const [debouncedCompanyQuery, setDebouncedCompanyQuery] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<Customer | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedCompanyQuery(companyQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [companyQuery]);

  const { data: searchResults, isLoading: isSearchingCompanies } = useSearchCustomers(
    debouncedCompanyQuery && debouncedCompanyQuery.length >= 2
      ? { registeredName: debouncedCompanyQuery, page: 0, size: 10 }
      : undefined,
    { query: { enabled: debouncedCompanyQuery.length >= 2 } }
  );

  // Fetch accounts for selected company
  const selectedCustomerNumber = selectedCompany?.customerNumber || '';
    const { data: targetAccounts, isLoading: isLoadingTargetAccounts } = useGetLedgerAccountsByCustomer(
    { customerNumber: selectedCustomerNumber },
    { query: { enabled: !!selectedCustomerNumber } }
  );

  // Auto-pick sole account if only one is available
  useEffect(() => {
    if (selectedCompany && Array.isArray(targetAccounts) && targetAccounts.length === 1) {
      const only = targetAccounts[0];
      setReallocateFundsForm((cur) => ({ ...cur, targetAccountId: only?.accountId || '' }));
    }
  }, [selectedCompany, targetAccounts]);


  const [rectifyLoanForm, setRectifyLoanForm] = useState<RectifyLoanFormState>({
    originalTxnId: '',
    targetAccountId: '',
    correctAmount: '',
    correctValueDate: toLocalDateString(),
    externalRef: '',
    remarks: ''
  });
  const [processingPenaltyId, setProcessingPenaltyId] = useState<string | null>(null);
  const [processingPenaltyAction, setProcessingPenaltyAction] = useState<'approve' | 'reject' | 'override' | 'prepare' | null>(null);

  // Update loading state based on mutation status
  useEffect(() => {
    setIsProcessingPayment(recordPaymentMutation.isPending);
  }, [recordPaymentMutation.isPending]);

  useEffect(() => {
    setIsProcessingEarlyRepayment(recordEarlyRepaymentMutation.isPending);
  }, [recordEarlyRepaymentMutation.isPending]);

  const [penaltyHistoryPage, setPenaltyHistoryPage] = useState(0);
  const [penaltyHistoryPageSize, setPenaltyHistoryPageSize] = useState(10);

  const approvePenaltyMutation = useApprovePenaltyReview();
  const rejectPenaltyMutation = useRejectPenaltyReview();
  const overridePenaltyMutation = useOverridePenaltyReview();
  const editPenaltyReviewMutation = useEditPenaltyReviewCharge();
  const calculatePenaltiesMutation = useCalculatePenalties({
    mutation: {
      onSuccess: (data: CalculatedPenalty) => {
        if (data.amountDec <= 0) {
          toast.info('No charges to apply — account is not overdue');
          return;
        }
        toast.success('Charges calculated successfully');
        refetchPendingPenalties();
        refetchPenaltyHistory();
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to calculate charges');
      }
    }
  });

  // Rectify payment mutation
  const rectifyPaymentMutation = useRectifyPayment({
    mutation: {
      onSuccess: () => {
        // Handle different success messages based on operation
        if (currentOperation === 'reallocate') {
          const fromName = loanAccount?.customerName || loanAccount?.accountNumber || 'source account';
          toast.success(`Payment successfully re-allocated from ${fromName} to target account`);
        } else if (currentOperation === 'rectify') {
          toast.success('Payment successfully rectified');
        } else if (editingLoan) {
          // Edit loan functionality
          toast.success('Loan edited successfully');
          setEditingLoan(null);
          
          // Close edit loan modal
          const editModal = document.getElementById('LoanEditModal');
          if (editModal) {
            const modal = BootstrapModal.getInstance(editModal);
            if (modal) modal.hide();
          }
        }
        
        fetchLoanAccount();
        fetchPaymentHistory();
        
        // Close existing modals
        const rectifyModal = document.getElementById('RectifyLoanModal');
        if (rectifyModal) {
          const modal = BootstrapModal.getInstance(rectifyModal);
          if (modal) modal.hide();
        }
        const reallocateModal = document.getElementById('ReallocateFundsModal');
        if (reallocateModal) {
          const modal = BootstrapModal.getInstance(reallocateModal);
          if (modal) modal.hide();
        }
        
        setIsProcessingRectify(false);
        setIsProcessingReallocate(false);
        resetRectifyForm();
        resetReallocateForm();
        setCurrentOperation(null);
      },
      onError: (error: any) => {
        const status = error?.response?.status;
        if (status === 409) {
          toast.error('This rectification was already submitted. Please refresh and check the transaction list.');
        } else if (error?.response?.data?.errors) {
          const validationErrors = error.response.data.errors;
          Object.keys(validationErrors).forEach(field => {
            toast.error(`${field}: ${validationErrors[field]}`);
          });
        } else {
          let fallback = 'Failed to process payment';
          if (currentOperation === 'reallocate') {
            fallback = 'Failed to re-allocate payment';
          } else if (currentOperation === 'rectify') {
            fallback = 'Failed to rectify payment';
          } else if (editingLoan) {
            fallback = 'Failed to edit loan';
          }
          toast.error(error?.response?.data?.message || error?.message || fallback);
        }
        setIsProcessingRectify(false);
        setIsProcessingReallocate(false);
        setCurrentOperation(null);
      }
    }
  });

  useEffect(() => {
    setIsProcessingRectify(rectifyPaymentMutation.isPending);
  }, [rectifyPaymentMutation.isPending]);

  useEffect(() => {
    setIsProcessingReallocate(rectifyPaymentMutation.isPending);
  }, [rectifyPaymentMutation.isPending]);

  // Get pending penalties for the account
  const { data: pendingPenaltiesPage, isLoading: isLoadingPendingPenalties, refetch: refetchPendingPenalties } = useListPenaltyReviews(
    { accountId: id || undefined, status: PenaltyReviewStatus.PENDING },
    { query: { enabled: !!id } }
  );
  const pendingPenalties: PenaltyReview[] = pendingPenaltiesPage?.content || [];

  // Add penalty history query (all penalties, excluding pending ones)
  const { data: penaltyHistoryData, isLoading: isLoadingPenaltyHistory, refetch: refetchPenaltyHistory } = useListPenaltyReviews(
    { accountId: id || undefined, page: penaltyHistoryPage, size: penaltyHistoryPageSize },
    { query: { enabled: !!id } }
  );
  // Filter out pending reviews from history
  const penaltyHistory: PenaltyReview[] = (penaltyHistoryData?.content || []).filter(
    (penalty) => penalty.status !== PenaltyReviewStatus.PENDING
  );

  // Initialize forms
  const methods = useForm<PaymentFormData>({
    resolver: zodResolver(paymentFormSchema),
    defaultValues: {
      paymentAmount: undefined,
      valueDate: toLocalDateString(),
      externalRef: '',
      remarks: ''
    }
  });

  const earlyRepaymentMethods = useForm<EarlyRepaymentFormData>({
    resolver: zodResolver(earlyRepaymentFormSchema),
    defaultValues: {
      valueDate: toLocalDateString(),
      externalRef: '',
      remarks: ''
    }
  });

  const { register, handleSubmit, formState: { errors }, reset } = methods;
  const { register: registerEarlyRepayment, handleSubmit: handleSubmitEarlyRepayment, formState: { errors: earlyRepaymentErrors }, reset: resetEarlyRepayment } = earlyRepaymentMethods;

  // Reset functions for rectify and reallocate forms
  const resetRectifyForm = () => {
    setRectifyLoanForm({
      originalTxnId: '',
      targetAccountId: '',
      correctAmount: '',
      correctValueDate: toLocalDateString(),
      externalRef: '',
      remarks: ''
    });
  };

  const resetReallocateForm = () => {
    setReallocateFundsForm({
      targetCustomerId: '',
      targetAccountId: '',
      amount: '',
      valueDate: toLocalDateString(),
      remarks: ''
    });
    setCompanyQuery('');
    setSelectedCompany(null);
  };

  // Discount / waiver agreed with the client (negotiated settlement, penalties waived)
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [isDownloadingLetter, setIsDownloadingLetter] = useState(false);
  // A discount captured in error, being reversed
  const [reverseDiscountTarget, setReverseDiscountTarget] = useState<LedgerTransaction | null>(null);

  const fetchLoanAccount = async () => {
    if (!id) {
      console.warn('Loan account ID is missing from URL');
      return;
    }

    try {
      setIsLoading(true);
      const response = await getLedgerAccountById(id);
      setLoanAccount(response);
    } catch (error) {
      console.error('Error fetching loan account:', error);
      toast.error('Failed to fetch loan account details');
      navigate('/loan-accounts');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPaymentHistory = async () => {
    if (!id) return;

    const normalizeTransactions = (response: unknown): LedgerTransaction[] => {
      if (!Array.isArray(response)) return [];
      return response.filter(
        (txn): txn is LedgerTransaction => !!txn && typeof txn === 'object'
      );
    };

    const isRectificationEntry = (txn: LedgerTransaction) =>
      txn.txnType === 'RECTIFIED' || txn.txnType === 'REVERSAL';

    const getRectifiedOriginalTxnIds = (transactions: LedgerTransaction[]): string[] => {
      return transactions
        .filter((txn) => isRectificationEntry(txn) && !!txn.reversedTxnId)
        .map((txn) => txn.reversedTxnId as string);
    };

    try {
      setIsLoadingPayments(true);
      const response = await getLedgerAccountTransactions(id);
      // Show only payment rows (exclude rectification/reversal entries)
      const transactions = normalizeTransactions(response);
      const linkedRectifiedTxnIds = getRectifiedOriginalTxnIds(transactions);
      // Payments, and discounts (they reduce what is owed too, and can be reversed from here). A
      // reversed discount stays listed, marked reversed; a rectified payment drops out as before.
      const paymentTransactions = transactions.filter(
        (txn: LedgerTransaction) =>
          (txn.txnType === 'PAYMENT' && !linkedRectifiedTxnIds.includes(txn.txnId))
          || txn.txnType === 'DISCOUNT'
      );
      // Build a map from original txnId to correction details
      const detailsMap: Record<string, { remarks?: string; externalRef?: string; targetAccountId?: string }> = {};
      transactions
        .filter((txn) => isRectificationEntry(txn) && !!txn.reversedTxnId)
        .forEach((txn) => {
          detailsMap[txn.reversedTxnId!] = {
            remarks: txn.remarks,
            externalRef: txn.externalRef,
            targetAccountId: txn.accountId,
          };
        });
      setRectificationDetailsMap(detailsMap);
      setRectifiedOriginalTxnIds(linkedRectifiedTxnIds);
      setPaymentHistory(paymentTransactions);
      // Store all transactions for audit trail (sorted newest first)
      const sortedAll = [...transactions].sort((a, b) => {
        const dateA = a.valueDate || a.createdAt || '';
        const dateB = b.valueDate || b.createdAt || '';
        return dateB.localeCompare(dateA);
      });
      setAllTransactions(sortedAll);
    } catch (error: any) {
      console.error('Error fetching payment history (full fetch):', error);
      // Fallback: load PAYMENT only (partial view) if full transaction history fails.
      try {
        const paymentOnlyResponse = await getLedgerAccountTransactions(id, { txnType: 'PAYMENT' as any });
        const paymentOnlyTransactions = normalizeTransactions(paymentOnlyResponse).filter(
          (txn) => txn.txnType === 'PAYMENT'
        );

        setPaymentHistory(paymentOnlyTransactions);
        setRectifiedOriginalTxnIds([]);
      } catch (fallbackError: any) {
        console.error('Error fetching payment history (PAYMENT-only fallback):', fallbackError);
        const backendMessage =
          fallbackError?.response?.data?.message ||
          fallbackError?.response?.data?.error ||
          fallbackError?.message;
        toast.error(backendMessage || 'Failed to fetch payment history');
        setPaymentHistory([]);
        setRectifiedOriginalTxnIds([]);
      }
    } finally {
      setIsLoadingPayments(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchLoanAccount();
      fetchPaymentHistory();
    } else {
      console.warn('LoanAccountDetails: No ID found in route parameters');
      setIsLoading(false);
    }
  }, [id]);

  // Set window references for InstallmentActions component
  useEffect(() => {
    (window as any).__currentLoanAccount = loanAccount;
    (window as any).__setEditingLoan = setEditingLoan;
    
    return () => {
      delete (window as any).__currentLoanAccount;
      delete (window as any).__setEditingLoan;
    };
  }, [loanAccount, setEditingLoan]);

  // Helper function to get payment status color and display text
  const getPaymentStatusInfo = (status?: string, paymentDate?: string) => {
    const today = new Date();
    const paymentDateTime = paymentDate ? new Date(paymentDate) : null;
    const isFuturePayment = paymentDateTime && paymentDateTime > today;

    switch (status) {
      case 'COMPLETED':
        return {
          color: 'success',
          text: 'Paid',
          icon: 'fa-check-circle'
        };
      case 'SCHEDULED':
        return {
          color: isFuturePayment ? 'info' : 'warning',
          text: isFuturePayment ? 'Scheduled' : 'Not Paid',
          icon: isFuturePayment ? 'fa-calendar' : 'fa-clock'
        };
      case 'FAILED':
        return {
          color: 'danger',
          text: 'Failed',
          icon: 'fa-times-circle'
        };
      case 'PARTIAL':
        return {
          color: 'warning',
          text: 'Partially Paid',
          icon: 'fa-exclamation-circle'
        };
      case 'OVERDUE':
        return {
          color: 'danger',
          text: 'Overdue',
          icon: 'fa-exclamation-triangle'
        };
      case 'CANCELLED':
        return {
          color: 'secondary',
          text: 'Cancelled',
          icon: 'fa-ban'
        };
      default:
        return {
          color: 'secondary',
          text: 'Unknown',
          icon: 'fa-question-circle'
        };
    }
  };

  const getLoanApplicationStage = (status?: string): LoanApplicationStatus => {
    switch (status) {
      case 'ACTIVE':
        return 'ACTIVE';
      case 'PAID':
        return 'COMPLETED';
      case 'CANCELLED':
        return 'CANCELLED';
      case 'DEFAULT':
      case 'PAST_DUE':
      case 'RESTRUCTURED':
      case 'WRITTEN_OFF':
        return 'ACTIVE';
      default:
        return 'ACTIVE';
    }
  };


























































































    // Fetch companies (customers) for re-allocation dropdown
    const getCustomerDisplayName = (c: any) => c?.info?.registeredName || c?.info?.tradingName || c?.customerNumber || c?.id;
    const getAccountDisplay = (a: any) => a?.accountNumber || a?.accountId?.substring(0, 8) || a?.accountId;



  // Define payment history table columns
  const hideModalById = (modalId: string) => {
    const modalElement = document.getElementById(modalId);
    if (!modalElement) return;

    const modal = BootstrapModal.getOrCreateInstance(modalElement);
    modal.hide();
  };

    const openReallocateFundsModal = (row: LedgerTransaction) => {
    setSelectedPaymentActionRow(row);
        setReallocateFundsForm({
      targetCustomerId: '',
      targetAccountId: '',
      amount: Math.abs(row.amountDec || 0).toString(),
      valueDate: row.valueDate ? row.valueDate.split('T')[0] : toLocalDateString(),
      remarks: row.remarks || ''
    });
    setCompanyQuery('');
    setSelectedCompany(null);



    const modalElement = document.getElementById('ReallocateFundsModal');
    if (modalElement) {
      const modal = new BootstrapModal(modalElement);
      modal.show();
    }
  };

  const openRectifyLoanModal = (row: LedgerTransaction) => {
    setSelectedPaymentActionRow(row);
    setRectifyLoanForm({
      originalTxnId: row.txnId,
      targetAccountId: row.accountId,
      correctAmount: Math.abs(row.amountDec || 0).toString(),
      correctValueDate: row.valueDate ? row.valueDate.split('T')[0] : toLocalDateString(),
      externalRef: row.externalRef || '',
      remarks: row.remarks || ''
    });

    const modalElement = document.getElementById('RectifyLoanModal');
    if (modalElement) {
      const modal = new BootstrapModal(modalElement);
      modal.show();
    }
  };

  const handlePaymentHistoryAction = (action: 'reallocate' | 'rectify', row: LedgerTransaction) => {
    if (action === 'reallocate') {
      openReallocateFundsModal(row);
      return;
    }

    openRectifyLoanModal(row);
  };

  const handleReallocateFundsSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!reallocateFundsForm.targetCustomerId.trim()) {
      toast.error('Please select a company.');
      return;
    }

    if (!reallocateFundsForm.targetAccountId.trim()) {
      toast.error('Please select a target account.');
      return;
    }

    setIsProcessingReallocate(true);
    setCurrentOperation('reallocate');

    const originalTxnId = selectedPaymentActionRow?.txnId;
    if (!originalTxnId) {
      toast.error('No payment transaction selected. Please close and try again.');
      setIsProcessingReallocate(false);
      return;
    }

    const request = {
      originalTxnId,
      targetAccountId: reallocateFundsForm.targetAccountId,
      correctAmount: Number(reallocateFundsForm.amount),
      correctValueDate: reallocateFundsForm.valueDate,
      externalRef: undefined,
      idempotencyKey: generateUUID(),
      remarks: reallocateFundsForm.remarks || undefined
    };

    rectifyPaymentMutation.mutate({ data: request });
  };


  const handleRectifyLoanSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const { originalTxnId, targetAccountId, correctValueDate, externalRef, remarks } = rectifyLoanForm;

    if (!originalTxnId) {
      toast.error('No payment transaction selected. Please close and try again.');
      return;
    }

    if (!targetAccountId) {
      toast.error('Target account is missing. Please close and try again.');
      return;
    }

    const parsedAmount = Number(rectifyLoanForm.correctAmount);
    if (!Number.isFinite(parsedAmount) || parsedAmount < 0.01) {
      toast.error('Correct amount must be at least 0.01.');
      return;
    }

    if (!correctValueDate) {
      toast.error('Correct value date is required.');
      return;
    }

    setIsProcessingRectify(true);
    setCurrentOperation('rectify');

    const request = {
      originalTxnId,
      targetAccountId,
      correctAmount: parsedAmount,
      correctValueDate: correctValueDate.split('T')[0],
      externalRef: externalRef || undefined,
      idempotencyKey: `rectify-${originalTxnId}-${Date.now()}`,
      remarks: remarks || undefined
    };

    rectifyPaymentMutation.mutate({ data: request });
  };

  const paymentColumns = (() => {
    const isRectificationEntry = (txn: LedgerTransaction) =>
      txn.txnType === 'RECTIFIED' || txn.txnType === 'REVERSAL';

    // Mark original payment rows as rectified based on linked rectification entries from API.
    const reversedTxnIds = new Set(rectifiedOriginalTxnIds);

    return [
      {
        name: 'Date',
        selector: (row: LedgerTransaction) => row.valueDate || '',
        sortable: true,
        cell: (row: LedgerTransaction) => (
          <div className="d-flex align-items-center">
            <div
              className="rounded-circle p-2 me-2"
              style={{
                backgroundColor: isRectificationEntry(row) ? '#fff3cd' : '#e7f3ff',
                border: `1px solid ${isRectificationEntry(row) ? '#ffc107' : '#b3d9ff'}`,
              }}
            >
              <i
                className={`fa ${isRectificationEntry(row) ? 'fa-undo text-warning' : 'fa-calendar text-primary'}`}
                style={{ display: 'block' }}
              ></i>
            </div>
            <div>
              <div className="fw-semibold">{formatDate(row.valueDate || '')}</div>
              {row.txnId && (
                <small className="text-muted">ID: {row.txnId.substring(0, 8)}</small>
              )}
            </div>
          </div>
        ),
        minWidth: '180px',
      },
      {
        name: 'Type',
        selector: (row: LedgerTransaction) => {
          if (isRectificationEntry(row)) return 'RECTIFICATION_ENTRY';
          if (row.txnType === 'DISCOUNT') return reversedTxnIds.has(row.txnId) ? 'REVERSED_DISCOUNT' : 'DISCOUNT';
          if (reversedTxnIds.has(row.txnId)) return 'RECTIFIED_PAYMENT';
          return 'PAYMENT';
        },
        sortable: true,
        cell: (row: LedgerTransaction) => {
          if (isRectificationEntry(row)) {
            return (
              <span className="badge bg-warning text-dark px-3 py-2">
                <i className="fa fa-repeat me-1"></i>
                Rectification Entry
              </span>
            );
          }

          if (row.txnType === 'DISCOUNT') {
            return reversedTxnIds.has(row.txnId) ? (
              <span className="badge bg-secondary px-3 py-2">
                <i className="fa fa-ban me-1"></i>
                Discount (reversed)
              </span>
            ) : (
              <span className="badge bg-primary px-3 py-2">
                <i className="fa fa-tag me-1"></i>
                Discount
              </span>
            );
          }

          if (reversedTxnIds.has(row.txnId)) {
            return (
              <span className="badge bg-secondary px-3 py-2">
                <i className="fa fa-ban me-1"></i>
                Rectified Payment
              </span>
            );
          }

          return (
            <span className="badge bg-info text-dark px-3 py-2">
              <i className="fa fa-money me-1"></i>
              Payment
            </span>
          );
        },
        minWidth: '190px',
      },
      {
        name: 'Status',
        selector: (row: LedgerTransaction) => row.txnType || '',
        sortable: true,
        cell: (row: LedgerTransaction) => {
          if (isRectificationEntry(row)) {
            return (
              <span className="badge bg-warning text-dark px-3 py-2">
                <i className="fa fa-undo me-1"></i>
                Rectified
              </span>
            );
          }
          if (reversedTxnIds.has(row.txnId)) {
            return (
              <span className="badge bg-secondary px-3 py-2">
                <i className="fa fa-ban me-1"></i>
                Rectified
              </span>
            );
          }
          return (
            <span className="badge bg-success px-3 py-2">
              <i className="fa fa-check me-1"></i>
              Active
            </span>
          );
        },
        minWidth: '130px',
      },
      {
        name: 'Bank Reference',
        selector: (row: LedgerTransaction) => row.externalRef || row.txnId || '',
        sortable: true,
        cell: (row: LedgerTransaction) => {
          return row.externalRef ? (
            <div className="d-flex align-items-center">
              <i className="fa fa-tag text-secondary me-2"></i>
              <span className="fw-semibold">{row.externalRef}</span>
            </div>
          ) : (
            <span className="text-muted">—</span>
          );
        },
        minWidth: '180px',
      },
      {
        name: 'Amount',
        selector: (row: LedgerTransaction) => row.amountDec || 0,
        sortable: true,
        cell: (row: LedgerTransaction) => {
          return (
            <div className="d-flex align-items-center">
              <div
                className="rounded-circle p-2 me-2"
                style={{
                  backgroundColor: '#d4edda',
                  border: '1px solid #c3e6cb',
                }}
              >
                <i
                  className="fa fa-money text-success"
                  style={{ display: 'block' }}
                ></i>
              </div>
              <div>
                <div className="fw-bold text-success">
                  {formatCurrency(Math.abs(row.amountDec || 0))}
                </div>
              </div>
            </div>
          );
        },
        minWidth: '150px',
      },

      {
        name: 'Actions',
        cell: (row: LedgerTransaction) => {
          // A discount not yet reversed can be undone
          if (row.txnType === 'DISCOUNT' && !reversedTxnIds.has(row.txnId)) {
            return (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger rounded-pill"
                onClick={() => setReverseDiscountTarget(row)}
                title="Reverse this discount"
              >
                <i className="fa fa-undo me-1"></i>Reverse
              </button>
            );
          }

          // Only allow rectify/reallocate on active PAYMENT rows
          const isActionable =
            row.txnType === 'PAYMENT' && !isRectificationEntry(row) && !reversedTxnIds.has(row.txnId);

          if (!isActionable) {
            return <span className="text-muted small">—</span>;
          }

          return (
            <Dropdown align="end">
              <Dropdown.Toggle
                variant="light"
                size="sm"
                id={`payment-actions-${row.txnId || row.externalRef || 'row'}`}
                className="rounded-pill d-flex align-items-center no-caret"
              >
                <span>Actions</span>
                <i className="fa fa-caret-down ms-2"></i>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item
                  className="action-reallocate"
                  onClick={() => handlePaymentHistoryAction('reallocate', row)}
                >
                  <i className="fa fa-random me-2"></i>
                  Re-allocate loan
                </Dropdown.Item>
                <Dropdown.Item
                  className="action-rectify"
                  onClick={() => handlePaymentHistoryAction('rectify', row)}
                >
                  <i className="fa fa-wrench me-2"></i>
                  Rectify Payment
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => {
                    setViewRemarksTarget(row);
                    setShowViewRemarksModal(true);
                  }}
                >
                  <i className="fa fa-comment me-2"></i>
                  View Remarks
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          );
        },
        minWidth: '170px',
        allowOverflow: true,
      },
    ];
  })();

  const handleOpenApproveModal = (penalty: PenaltyReview) => {
    setSelectedPenalty(penalty);
    setApprovalDueDate(getPreferredDateInputValue(
      // newDueDate is not declared on the penalty response schemas; read defensively so the
      // fallback below still applies if the API starts returning it.
      (penalty?.calculated as any)?.newDueDate,
      (penalty as any)?.newDueDate || loanAccount?.firstDueDate || ''
    ));
    setShowApproveModal(true);

    // Show the modal using Bootstrap's Modal class
    const modalElement = document.getElementById('ApprovePenaltyModal');
    if (modalElement) {
      const modal = new BootstrapModal(modalElement);
      modal.show();
    }
  };

  const handleApprove = async () => {
    if (!selectedPenalty || !selectedPenalty.id) {
      toast.error('Invalid charge review ID');
      return;
    }
    try {
      setProcessingPenaltyId(selectedPenalty.id);
      setProcessingPenaltyAction('approve');
      await approvePenaltyMutation.mutateAsync({
        reviewId: selectedPenalty.id,
        data: {
          ...(approvalDueDate && { newDueDate: approvalDueDate }),
          remarks: 'Approved by loan officer'
        }
      });
      toast.success('Charge approved successfully');

      // Close modal only after successful response
      const modalElement = document.getElementById('ApprovePenaltyModal');
      if (modalElement) {
        const modal = BootstrapModal.getInstance(modalElement);
        if (modal) modal.hide();
      }
      setShowApproveModal(false);
      setApprovalDueDate('');
      // Refresh all data after successful approval
      await Promise.all([
        refetchPendingPenalties(),
        refetchPenaltyHistory(),
        fetchLoanAccount(),
        fetchPaymentHistory()
      ]);
    } catch (error) {
      console.error('Error approving charge:', error);
      toast.error('Failed to approve charge');
    } finally {
      setProcessingPenaltyId(null);
      setProcessingPenaltyAction(null);
    }
  };

  const handleOpenRejectModal = (penalty: PenaltyReview) => {
    setSelectedPenalty(penalty);
    setRejectionReason('');
    setShowRejectModal(true);

    // Show the modal using Bootstrap's Modal class
    const modalElement = document.getElementById('RejectPenaltyModal');
    if (modalElement) {
      const modal = new BootstrapModal(modalElement);
      modal.show();
    }
  };

  const handleReject = async () => {
    if (!selectedPenalty || !selectedPenalty.id) return;

    try {
      setProcessingPenaltyId(selectedPenalty.id);
      setProcessingPenaltyAction('reject');
      await rejectPenaltyMutation.mutateAsync({
        reviewId: selectedPenalty.id,
        data: {
          remarks: rejectionReason
        }
      });
      toast.success('Charge rejected successfully');

      // Close modal only after successful response
      const modalElement = document.getElementById('RejectPenaltyModal');
      if (modalElement) {
        const modal = BootstrapModal.getInstance(modalElement);
        if (modal) modal.hide();
      }
      setShowRejectModal(false);
      setRejectionReason('');
      // Refresh all data after successful rejection
      await Promise.all([
        refetchPendingPenalties(),
        refetchPenaltyHistory(),
        fetchLoanAccount(),
        fetchPaymentHistory()
      ]);
    } catch (error) {
      console.error('Error rejecting charge:', error);
      toast.error('Failed to reject charge');
    } finally {
      setProcessingPenaltyId(null);
      setProcessingPenaltyAction(null);
    }
  };

  const handleOverride = async () => {
    if (!selectedPenalty || !selectedPenalty.id) return;

      const reviewId = selectedPenalty.id;

    try {
      setProcessingPenaltyId(reviewId);
      setProcessingPenaltyAction('override');
      if (isHistoryEdit) {
        await editPenaltyReviewMutation.mutateAsync({
          reviewId,
          data: {
            amountDec: overrideAmount,
            remarks: overrideReason,
            // Required by the edit endpoint to guard against duplicate edits. The previous
            // hand-written client omitted it entirely.
            idempotencyKey: generateUUID(),
            asOfDate: overrideAsOfDate || toLocalDateString(),
            ...(overrideDueDate ? { newDueDate: overrideDueDate } : {})
          }
        });
      } else {
        await overridePenaltyMutation.mutateAsync({
          reviewId,
          data: {
            overrideAmount,
            remarks: overrideReason,
            ...(overrideAsOfDate ? { asOfDate: overrideAsOfDate } : {}),
            ...(overrideDueDate ? { newDueDate: overrideDueDate } : {})
          }
        });
      }
      toast.success(isHistoryEdit ? 'Charge edited successfully' : 'Charge overridden successfully');

      // Close the modal using Bootstrap's Modal class
      const modalElement = document.getElementById('DueCollectionOverride');
      if (modalElement) {
        const modal = BootstrapModal.getInstance(modalElement);
        if (modal) {
          modal.hide();
        }
      }

      setShowOverrideModal(false);
      setOverrideAmount(0);
      setOverrideReason('');
      setOverrideDueDate('');
      setOverrideAsOfDate('');
      // Refresh all data after successful override
      await Promise.all([
        refetchPendingPenalties(),
        refetchPenaltyHistory(),
        fetchLoanAccount(),
        fetchPaymentHistory()
      ]);
    } catch (error) {
      console.error('Error overriding charge:', error);
      toast.error('Failed to override charge');
    } finally {
      setProcessingPenaltyId(null);
      setProcessingPenaltyAction(null);
      setIsPreparingOverride(false);
      setIsHistoryEdit(false);
    }
  };

  const handleOpenOverrideModal = async (penalty: PenaltyReview) => {
    setIsHistoryEdit(penalty.status !== PenaltyReviewStatus.PENDING);
    setSelectedPenalty(penalty);
    setOverrideAmount(penalty.calculated?.amountDec || 0);
    setOverrideAsOfDate(penalty.calculated?.asOfDate || toLocalDateString());
    setShowOverrideModal(true);
    setOverrideDueDate('');

    const modalElement = document.getElementById('DueCollectionOverride');
    if (modalElement) {
      const modal = new BootstrapModal(modalElement);
      modal.show();
    }
  };

  const [showChargesModal, setShowChargesModal] = useState(false);
  const [chargesAsOfDate, setChargesAsOfDate] = useState(toLocalDateString());
  const [chargesNewDueDate, setChargesNewDueDate] = useState('');
  const [chargesManualAmount, setChargesManualAmount] = useState('');
  const [chargesStrategy, setChargesStrategy] = useState<'DEFAULT' | 'OVERRIDE_RATE'>('DEFAULT');
  const [calculationResult, setCalculationResult] = useState<CalculatedPenalty | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);

  // Helper function to get the label based on baseAmountType
  const getBaseAmountLabel = (baseAmountType: string | undefined): string => {
    switch (baseAmountType) {
      case 'PRINCIPAL':
        return 'principal balance';
      case 'TOTAL_OUTSTANDING':
        return 'outstanding balance';
      case 'INSTALLMENT_TOTAL':
        return 'overdue installment total';
      default:
        return 'outstanding balance';
    }
  };

  const handleCalculatePenalties = async () => {
    if (!loanAccount?.accountId && !id) {
      toast.error('Account ID is required to calculate charges');
      return;
    }

    setIsCalculating(true);
    try {
      const data: any = {
        accountId: loanAccount?.accountId || id || '',
        asOfDate: chargesAsOfDate
      };

      if (chargesNewDueDate) {
        data.newDueDate = chargesNewDueDate;
      }

      if (chargesStrategy === 'OVERRIDE_RATE' && chargesManualAmount) {
        data.strategy = 'OVERRIDE_RATE';
        data.overrideRatePercent = Number(chargesManualAmount);
      }

      const result = await calculatePenaltiesMutation.mutateAsync({ data });
      setCalculationResult(result);
    } catch (error) {
      console.error('Error calculating penalties:', error);
      toast.error('Failed to calculate charges');
    } finally {
      setIsCalculating(false);
    }
  };

  // Reset calculation result when modal closes
  useEffect(() => {
    if (!showChargesModal) {
      setCalculationResult(null);
      setIsCalculating(false);
    }
  }, [showChargesModal]);

  // Helper functions to check if payments are allowed
  const canMakePayment = (account: LedgerAccount | null) => {
    if (!account) return false;
    const allowedStatuses = ['ACTIVE', 'IN_ARREARS', 'DEFAULTED'];
    const isAllowedStatus = allowedStatuses.includes(account.status || '');
    const hasOutstanding = (account.totalOutstanding || 0) > 0;
    return isAllowedStatus && hasOutstanding;
  };

  const getPaymentDisabledReason = (account: LedgerAccount | null) => {
    if (!account) return 'Account information is not available';
    const allowedStatuses = ['ACTIVE', 'IN_ARREARS', 'DEFAULTED'];
    if (!allowedStatuses.includes(account.status || '')) {
      return `Payments are not allowed for accounts with status: ${account.status}`;
    }
    if ((account.totalOutstanding || 0) <= 0) {
      return 'Payments are not allowed when outstanding amount is zero';
    }
    return '';
  };

  // Helper function to get current due date from installments
  const getCurrentDueDate = (account: LedgerAccount | null): string => {
    if (!account?.installments || account.installments.length === 0) {
      return account?.firstDueDate ? formatDate(account.firstDueDate) : 'N/A';
    }

    // The agreed due date of the next unpaid instalment. A charge moves the date an instalment is
    // payable by, but the loan's due date stays the one in the schedule.
    const unpaidInstallment = account.installments.find(
      inst => inst.status === 'PENDING' || inst.status === 'DUE' || inst.status === 'OVERDUE' || inst.status === 'PARTIALLY_PAID'
    );
    if (unpaidInstallment && agreedDueDate(unpaidInstallment)) {
      return formatDate(agreedDueDate(unpaidInstallment));
    }

    // If no unpaid installment found, get the earliest future installment
    const futureInstallments = account.installments
      .filter(inst => agreedDueDate(inst) && new Date(agreedDueDate(inst)) >= new Date())
      .sort((a, b) => new Date(agreedDueDate(a)).getTime() - new Date(agreedDueDate(b)).getTime());
    if (futureInstallments.length > 0) {
      return formatDate(agreedDueDate(futureInstallments[0]));
    }

    // Fallback to the last installment's due date
    const sortedInstallments = [...account.installments]
      .sort((a, b) => new Date(agreedDueDate(b)).getTime() - new Date(agreedDueDate(a)).getTime());
    if (sortedInstallments.length > 0 && agreedDueDate(sortedInstallments[0])) {
      return formatDate(agreedDueDate(sortedInstallments[0]));
    }

    // Final fallback to firstDueDate
    return account.firstDueDate ? formatDate(account.firstDueDate) : 'N/A';
  };

  // Helper function to get installment amount from the next installment
  const getInstallmentAmount = (account: LedgerAccount | null): number => {
    if (!account?.installments || account.installments.length === 0) {
      return 0;
    }

    // Find the next unpaid installment (PENDING, DUE, or OVERDUE)
    const unpaidInstallment = account.installments.find(
      inst => inst.status === 'PENDING' || inst.status === 'DUE' || inst.status === 'OVERDUE'
    );
    if (unpaidInstallment) {
      return (unpaidInstallment.principalDue || 0) + (unpaidInstallment.interestDue || 0) + (unpaidInstallment.feesDue || 0);
    }

    // If no unpaid installment found, get the earliest future installment
    const futureInstallments = account.installments
      .filter(inst => inst.dueDate && new Date(inst.dueDate) >= new Date())
      .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
    if (futureInstallments.length > 0) {
      const installment = futureInstallments[0];
      return (installment.principalDue || 0) + (installment.interestDue || 0) + (installment.feesDue || 0);
    }

    // Fallback to the first installment
    if (account.installments.length > 0) {
      const firstInstallment = account.installments[0];
      return (firstInstallment.principalDue || 0) + (firstInstallment.interestDue || 0) + (firstInstallment.feesDue || 0);
    }

    return 0;
  };

  // Add penalty columns definition
  const penaltyColumns = [
    {
      name: 'As Of Date',
      selector: (row: PenaltyReview) => row.calculated?.asOfDate || '',
      sortable: true,
      cell: (row: PenaltyReview) => (
        <div className="d-flex align-items-center">
          <div className="rounded-circle p-2 me-2" style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107' }}>
            <i className="fa fa-calendar text-warning" style={{ display: 'block' }}></i>
          </div>
          <div>
            <div className="fw-semibold">{formatDate(row.calculated?.asOfDate || '')}</div>
          </div>
        </div>
      ),
      minWidth: '160px',
    },
    {
      name: 'Calculated Date',
      selector: (row: PenaltyReview) => row.createdAt || '',
      sortable: true,
      cell: (row: PenaltyReview) => (
        <div className="d-flex align-items-center">
          <div className="rounded-circle p-2 me-2" style={{ backgroundColor: '#e7f3ff', border: '1px solid #b3d9ff' }}>
            <i className="fa fa-calculator text-info" style={{ display: 'block' }}></i>
          </div>
          <div>
            <div className="fw-semibold">{formatDate(row.createdAt || '')}</div>
            {row.createdAt && (
              <small className="text-muted">Review created</small>
            )}
          </div>
        </div>
      ),
      minWidth: '180px',
    },
    {
      name: 'Charge Amount',
      selector: (row: PenaltyReview) => row.calculated?.amountDec || 0,
      sortable: true,
      cell: (row: PenaltyReview) => (
        <div className="d-flex align-items-center">
          <div className="rounded-circle p-2 me-2" style={{ backgroundColor: '#f8d7da', border: '1px solid #f5c6cb' }}>
            <i className="fa fa-exclamation-circle text-danger" style={{ display: 'block' }}></i>
          </div>
          <div>
            <div className="fw-bold text-danger">{formatCurrency(row.calculated?.amountDec || 0)}</div>
          </div>
        </div>
      ),
      minWidth: '150px',
    },
    {
      name: 'Status',
      selector: (row: PenaltyReview) => row.status || '',
      sortable: true,
      cell: (row: PenaltyReview) => {
        const getStatusColor = (status?: string) => {
          switch (status) {
            case 'PENDING':
              return { bg: 'warning', icon: 'fa-clock' };
            case 'APPROVED':
              return { bg: 'success', icon: 'fa-check-circle' };
            case 'REJECTED':
              return { bg: 'danger', icon: 'fa-times-circle' };
            case 'OVERRIDDEN':
              return { bg: 'info', icon: 'fa-edit' };
            case 'REVERSED':
              return { bg: 'dark', icon: 'fa-undo' };
            default:
              return { bg: 'secondary', icon: 'fa-circle' };
          }
        };

        const statusInfo = getStatusColor(row.status);
        return (
          <span className={`badge bg-${statusInfo.bg} px-3 py-2 d-inline-flex align-items-center gap-1`}>
            <i className={`fa ${statusInfo.icon}`}></i>
            {row.status}
          </span>
        );
      },
      minWidth: '140px',
    },
    {
      name: 'Actions',
      cell: (row: PenaltyReview) => {
        if (row.status !== PenaltyReviewStatus.PENDING) {
          const statusLabels: Record<string, string> = {
            [PenaltyReviewStatus.APPROVED]: 'Approved',
            [PenaltyReviewStatus.REJECTED]: 'Rejected',
            [PenaltyReviewStatus.OVERRIDDEN]: 'Overridden',
            REVERSED: 'Reversed'
          };
          return (
            <span className="badge bg-secondary px-3 py-2">
              <i className="fa fa-lock me-1"></i>
              {statusLabels[row.status] || row.status}
            </span>
          );
        }

        const isProcessingThisRow = processingPenaltyId === row.id;
        const isApproving = isProcessingThisRow && processingPenaltyAction === 'approve';
        const isRejecting = isProcessingThisRow && processingPenaltyAction === 'reject';
        const isOverriding = isProcessingThisRow && processingPenaltyAction === 'override';

        return (
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-success rounded-circle"
              onClick={() => {
                if (!isProcessingThisRow) {
                  handleOpenApproveModal(row);
                }
              }}
              disabled={isProcessingThisRow}
              title="Approve Charge"
              aria-label="Approve charge"
              style={{
                width: '36px',
                height: '36px',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative'
              }}
            >
              {isApproving ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '14px', height: '14px', borderWidth: '2px' }}></span>
              ) : (
              <i className="fa fa-check"></i>
              )}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-danger rounded-circle"
              onClick={() => {
                if (!isProcessingThisRow) {
                  handleOpenRejectModal(row);
                }
              }}
              disabled={isProcessingThisRow}
              title="Reject Charge"
              aria-label="Reject charge"
              style={{
                width: '36px',
                height: '36px',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative'
              }}
            >
              {isRejecting ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '14px', height: '14px', borderWidth: '2px' }}></span>
              ) : (
              <i className="fa fa-times"></i>
              )}
            </button>
            <button
              className="btn btn-sm btn-info rounded-circle"
              onClick={() => {
                if (!isProcessingThisRow) {
                  handleOpenOverrideModal(row);
                }
              }}
              disabled={isProcessingThisRow}
              title="Override Charge Amount"
              style={{
                width: '36px',
                height: '36px',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative'
              }}
            >
              {isOverriding ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: '14px', height: '14px', borderWidth: '2px' }}></span>
              ) : (
              <i className="fa fa-edit"></i>
              )}
            </button>
          </div>
        );
      },
      minWidth: '140px',
    },
  ];

  // Add payment handlers
  const handleOpenPaymentModal = () => {
    // Prevent opening if payments are not allowed
    if (!canMakePayment(loanAccount)) {
      toast.warning(getPaymentDisabledReason(loanAccount));
      return;
    }

    reset({
      paymentAmount: loanAccount?.totalOutstanding?.toString() || '',
      valueDate: toLocalDateString(),
      externalRef: '',
      remarks: ''
    });

    setShowPaymentModal(true);
    const modalElement = document.getElementById('LoanAccountPayment');
    if (modalElement) {
      const modal = new BootstrapModal(modalElement);
      modal.show();
    }
  };

  const handleProcessPayment = async (data: PaymentFormData) => {
    if (!loanAccount?.accountId && !id) return;

    try {
      setIsProcessingPayment(true);
      const paymentRequest = {
        accountId: loanAccount?.accountId || id || '',
        amount: Number(data.paymentAmount),
        valueDate: data.valueDate,
        idempotencyKey: `payment-${loanAccount?.accountId || id}-${Date.now()}`,
        externalRef: data.externalRef || undefined,
        remarks: data.remarks || undefined
      };

      // The mutation will handle closing the modal on success
      recordPaymentMutation.mutate({ data: paymentRequest });
    } catch (error) {
      console.error('Error processing payment:', error);
      toast.error('Failed to process payment');
      setIsProcessingPayment(false);
    }
  };

  // Add early repayment handlers
  const handleOpenEarlyRepaymentModal = async () => {
    // Prevent opening if payments are not allowed
    if (!canMakePayment(loanAccount)) {
      toast.warning(getPaymentDisabledReason(loanAccount));
      return;
    }

    setShowEarlyRepaymentModal(true);
    setEarlyRepaymentCalculation(null);

    try {
      // Get loan account details first
      if (!loanAccount && id) {
        const loanAccountResponse = await getLedgerAccountById(id);
      setLoanAccount(loanAccountResponse);
      }

      // Show the modal
      const modalElement = document.getElementById('LoanAccountEarlyRepayment');
      if (modalElement) {
        const modal = new BootstrapModal(modalElement);
        modal.show();
      }
    } catch (error) {
      console.error('Error preparing early repayment:', error);
      toast.error('Failed to prepare early repayment');
      setShowEarlyRepaymentModal(false);
    }
  };

  const handleCalculateEarlyRepayment = async () => {
    if (!loanAccount?.accountId && !id) return;
    setIsCalculatingEarlyRepayment(true);
    try {
      getEarlyRepaymentQuoteMutation.mutate({
        data: {
          accountId: loanAccount?.accountId || id || '',
          quoteDate: toLocalDateString()
        }
      });
    } catch (error) {
      console.error('Error calculating early repayment:', error);
      setIsCalculatingEarlyRepayment(false);
    }
  };

  // Update early repayment calculation when mutation succeeds
  useEffect(() => {
    if (getEarlyRepaymentQuoteMutation.isSuccess && getEarlyRepaymentQuoteMutation.data) {
      setEarlyRepaymentCalculation(getEarlyRepaymentQuoteMutation.data);
      setIsCalculatingEarlyRepayment(false);

      // Set default form values
      resetEarlyRepayment({
        valueDate: toLocalDateString(),
        externalRef: '',
        remarks: ''
      });
    }
  }, [getEarlyRepaymentQuoteMutation.isSuccess, getEarlyRepaymentQuoteMutation.data, resetEarlyRepayment]);

  const handleProcessEarlyRepayment = async (data: EarlyRepaymentFormData) => {
    if (!loanAccount?.accountId && !id) return;

    try {
      setIsProcessingEarlyRepayment(true);
      const earlyRepaymentRequest = {
        accountId: loanAccount?.accountId || id || '',
        valueDate: data.valueDate,
        idempotencyKey: `early-repayment-${loanAccount?.accountId || id}-${Date.now()}`,
        externalRef: data.externalRef || undefined,
        remarks: data.remarks || undefined
      };

      // The mutation will handle closing the modal on success
      recordEarlyRepaymentMutation.mutate({ data: earlyRepaymentRequest });
    } catch (error) {
      console.error('Error processing early repayment:', error);
      toast.error('Failed to process early repayment');
      setIsProcessingEarlyRepayment(false);
    }
  };

  // Add penalty history columns
  const penaltyHistoryColumns = [
    {
      name: 'As Of Date',
      selector: (row: PenaltyReview) => row.calculated?.asOfDate || '',
      sortable: true,
      cell: (row: PenaltyReview) => (
        <div className="d-flex align-items-center">
          <div className="rounded-circle p-2 me-2" style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107' }}>
            <i className="fa fa-calendar text-warning" style={{ display: 'block' }}></i>
          </div>
          <div>
            <div className="fw-semibold">{formatDate(row.calculated?.asOfDate || '')}</div>
          </div>
        </div>
      ),
      minWidth: '160px',
    },
    {
      name: 'Calculated Date',
      selector: (row: PenaltyReview) => row.createdAt || '',
      sortable: true,
      cell: (row: PenaltyReview) => (
        <div className="d-flex align-items-center">
          <div className="rounded-circle p-2 me-2" style={{ backgroundColor: '#e7f3ff', border: '1px solid #b3d9ff' }}>
            <i className="fa fa-calculator text-info" style={{ display: 'block' }}></i>
          </div>
          <div>
            <div className="fw-semibold">{formatDate(row.createdAt || '')}</div>
            {row.createdAt && (
              <small className="text-muted">Review created</small>
            )}
          </div>
        </div>
      ),
      minWidth: '180px',
    },
    {
      name: 'Charge Amount',
      selector: (row: PenaltyReview) => row.calculated?.amountDec || 0,
      sortable: true,
      cell: (row: PenaltyReview) => (
        <div className="d-flex align-items-center">
          <div className="rounded-circle p-2 me-2" style={{ backgroundColor: '#f8d7da', border: '1px solid #f5c6cb' }}>
            <i className="fa fa-exclamation-circle text-danger" style={{ display: 'block' }}></i>
          </div>
          <div>
            <div className="fw-bold text-danger">{formatCurrency(row.calculated?.amountDec || 0)}</div>
          </div>
        </div>
      ),
      minWidth: '150px',
    },
    {
      name: 'Status',
      selector: (row: PenaltyReview) => row.status || '',
      sortable: true,
      cell: (row: PenaltyReview) => {
        const getStatusColor = (status?: string) => {
          switch (status) {
            case 'PENDING':
              return { bg: 'warning', icon: 'fa-clock' };
            case 'APPROVED':
              return { bg: 'success', icon: 'fa-check-circle' };
            case 'REJECTED':
              return { bg: 'danger', icon: 'fa-times-circle' };
            case 'OVERRIDDEN':
              return { bg: 'info', icon: 'fa-edit' };
            case 'REVERSED':
              return { bg: 'dark', icon: 'fa-undo' };
            default:
              return { bg: 'secondary', icon: 'fa-circle' };
          }
        };

        const statusInfo = getStatusColor(row.status);
        return (
          <span className={`badge bg-${statusInfo.bg} px-3 py-2 d-inline-flex align-items-center gap-1`}>
            <i className={`fa ${statusInfo.icon}`}></i>
            {row.status}
          </span>
        );
      },
      minWidth: '140px',
    },
    {
      name: 'Created At',
      selector: (row: PenaltyReview) => row.createdAt || '',
      sortable: true,
      cell: (row: PenaltyReview) => (
        <div className="d-flex align-items-center">
          <i className="fa fa-clock-o text-muted me-2"></i>
          <span>{row.createdAt ? formatDate(row.createdAt) : '—'}</span>
        </div>
      ),
      minWidth: '150px',
    },
    {
      name: 'Actions',
      cell: (row: PenaltyReview) => {
        const isProcessingThisRow =
          processingPenaltyId === row.id &&
          (processingPenaltyAction === 'override' || processingPenaltyAction === 'prepare');
        return (
          <Dropdown align="end">
            <Dropdown.Toggle
              variant="light"
              size="sm"
              id={`charge-actions-${row.id}`}
              className="rounded-pill"
              disabled={isProcessingThisRow || isPreparingOverride}
            >
              Actions
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() => handleOpenOverrideModal(row)}
                disabled={isProcessingThisRow || row.status === 'REVERSED'}
              >
                <i className="fa fa-edit me-2"></i>
                Edit charge
              </Dropdown.Item>
              {/* #37: take an approved charge back off the account, with a reason */}
              <Dropdown.Item
                onClick={() => { setReversing(row); setReverseReason(''); }}
                disabled={isProcessingThisRow || (row.status !== 'APPROVED' && row.status !== 'OVERRIDDEN')}
              >
                <i className="fa fa-undo me-2"></i>
                Reverse charge
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        );
      },
      minWidth: '150px',
    }
  ];

  // Reverse an approved charge (#37)
  const [reversing, setReversing] = useState<PenaltyReview | null>(null);
  const [reverseReason, setReverseReason] = useState('');
  const [isReversing, setIsReversing] = useState(false);
  const refetchAfterChargeChange = () => Promise.all([
    refetchPendingPenalties(), refetchPenaltyHistory(), fetchLoanAccount(), fetchPaymentHistory()
  ]);
  const confirmReverse = async () => {
    if (!reversing?.id || !reverseReason.trim()) { toast.error('Give a reason for reversing the charge'); return; }
    setIsReversing(true);
    try {
      await reverseChargeRequest(reversing.id, reverseReason.trim());
      toast.success('Charge reversed');
      setReversing(null);
      refetchAfterChargeChange();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'The charge could not be reversed');
    } finally {
      setIsReversing(false);
    }
  };

  // Handle penalty history pagination
  const handlePenaltyHistoryPageChange = (newPage: number) => {
    setPenaltyHistoryPage(newPage - 1);
  };

  const handlePenaltyHistoryPerRowsChange = (newPerPage: number) => {
    setPenaltyHistoryPageSize(newPerPage);
    setPenaltyHistoryPage(0);
  };

  if (isLoading) {
    return (
      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
        <div className="container-fluid">
      <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="visually-hidden">Loading...</span>
        </div>
            <p className="mt-3 text-muted">Loading loan account details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!id) {
    return (
      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
        <div className="container-fluid">
          <div className="text-center py-5">
            <i className="fa fa-exclamation-triangle fa-3x text-warning mb-3"></i>
            <h5>Invalid loan account ID</h5>
            <p className="text-muted">The account ID is missing from the URL.</p>
            <button className="btn btn-primary mt-3" onClick={() => navigate('/loan-accounts')}>
              <i className="fa fa-arrow-left me-2"></i>
              Back to Loan Accounts
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!loanAccount) {
    return (
      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
        <div className="container-fluid">
      <div className="text-center py-5">
            <i className="fa fa-file-o fa-3x text-muted mb-3"></i>
            <h5>Loan account not found</h5>
            <p className="text-muted">The requested loan account could not be found.</p>
            <button className="btn btn-primary mt-3" onClick={() => navigate('/loan-accounts')}>
              <i className="fa fa-arrow-left me-2"></i>
              Back to Loan Accounts
        </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if payments are allowed for the current account
  const paymentsAllowed = canMakePayment(loanAccount);
  // Settled and closed (US-38): nothing more is owed, so no charges or discounts; the letter confirms it
  const isSettled = loanAccount?.status === 'CLOSED';
  const settledHow = loanAccount?.closureReason === 'EARLY_SETTLEMENT' ? 'settled early' : 'paid off';

  const handleDownloadSettlementLetter = async () => {
    if (!loanAccount?.accountId) return;
    setIsDownloadingLetter(true);
    try {
      const blob = await downloadSettlementLetter(loanAccount.accountId);
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Settlement_letter_${(loanAccount.accountNumber || loanAccount.accountId).replace(/[^A-Za-z0-9-]/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Could not produce the settlement letter');
    } finally {
      setIsDownloadingLetter(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Modal show={!!reversing} onHide={() => !isReversing && setReversing(null)} centered>
        <Modal.Header closeButton><Modal.Title className="fs-6">Reverse this charge?</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="small">The charge is taken back off the account: the balance and the instalment it sits on drop by it. It stays in the list as Reversed. A due date or maturity date it moved is not moved back.</p>
          <label htmlFor="reverseReason" className="form-label small">Reason <span className="text-danger">*</span></label>
          <input id="reverseReason" type="text" maxLength={500} className="form-control" value={reverseReason}
            placeholder="e.g. Client paid on time; the bank's payment was delayed" onChange={e => setReverseReason(e.target.value)} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setReversing(null)} disabled={isReversing}>Cancel</Button>
          <Button variant="danger" size="sm" onClick={confirmReverse} disabled={isReversing || !reverseReason.trim()}>{isReversing ? 'Reversing…' : 'Reverse charge'}</Button>
        </Modal.Footer>
      </Modal>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {/* Loan Edit Modal */}
      {editingLoan && (
        <div className="modal fade" id="LoanEditModal" tabIndex={-1} aria-hidden="true">
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <LoanEditModal
              applicationId={loanAccount?.applicationId || ''}
              loan={{
                loanAmount: editingLoan.loanAmount,
                interestRate: editingLoan.interestRate,
                dueDate: editingLoan.dueDate,
                commencementDate: editingLoan.commencementDate,
                adminFee: editingLoan.adminFee,
                customerName: editingLoan.customerName,
                accountNumber: editingLoan.accountNumber,
              }}
              hasCharges={penaltyHistory.length > 0 || pendingPenalties.length > 0}
              onSuccess={() => {
                // Refresh all page data after loan update
                fetchLoanAccount();
                fetchPaymentHistory();
                refetchPendingPenalties();
                refetchPenaltyHistory();
                setEditingLoan(null);
              }}
              onClose={() => setEditingLoan(null)}
            />
          </div>
        </div>
      )}

      {/* Header */}
      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-4 bg-primary-gradient">
        <div className="container-fluid">
          <div className="row g-3 mb-3 align-items-center">
            <div className="col">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb bg-transparent mb-0">
                  <li className="breadcrumb-item"><Link className="text-secondary" to="/">Home</Link></li>
                  <li className="breadcrumb-item"><Link className="text-secondary" to="/loan-accounts">Loan Accounts</Link></li>
                  <li className="breadcrumb-item active" aria-current="page">Account Details</li>
                </ol>
              </nav>
              {loanAccount && (
                <>
                  <h2 className="mb-1 fw-bold">{loanAccount.customerName || 'Loan Account'}</h2>
                  <p className="text-muted mb-0">
                    <i className="fa fa-file-text me-2"></i>
                    {loanAccount.accountNumber || loanAccount.accountId.substring(0, 8)}
                  </p>
                </>
              )}
            </div>
            <div className="col-auto">
              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-light"
                  onClick={() => navigate(-1)}
                >
                  <i className="fa fa-arrow-left me-2"></i>
                  Back
                </button>
                <button
                  className="btn btn-success shadow-sm"
                  onClick={handleOpenPaymentModal}
                  disabled={!paymentsAllowed}
                  title={!paymentsAllowed ? getPaymentDisabledReason(loanAccount) : 'Make a payment for this account'}
                >
                  <i className="fa fa-credit-card me-2"></i>
                  Make Payment
                </button>
                <button
                  className="btn btn-warning shadow-sm"
                  onClick={() => {
                    setChargesAsOfDate(toLocalDateString());
                    setChargesNewDueDate('');
                    setChargesManualAmount('');
                    setChargesStrategy('DEFAULT');
                    setShowChargesModal(true);
                  }}
                  disabled={isSettled || calculatePenaltiesMutation.isPending || (!loanAccount?.accountId && !id)}
                  title={isSettled ? 'This loan is settled; no further charges' : 'Calculate charges for this account'}
                >
                  {calculatePenaltiesMutation.isPending ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Calculating...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-calculator me-2"></i>
                      Calculate Charges
                    </>
                  )}
                </button>
                <button
                  className="btn btn-outline-primary shadow-sm"
                  onClick={() => setShowDiscountModal(true)}
                  disabled={isSettled || !loanAccount?.accountId}
                  title={isSettled ? 'This loan is settled; nothing is left to discount' : 'Apply an agreed discount or waiver to this account'}
                >
                  <i className="fa fa-tag me-2"></i>
                  Discount / Waiver
                </button>
                <button
                  className="btn btn-outline-secondary shadow-sm"
                  onClick={() => setShowStatementModal(true)}
                  disabled={!loanAccount?.accountId}
                  title="Produce a statement for the client"
                >
                  <i className="fa fa-file-text-o me-2"></i>
                  Statement
                </button>
                {isSettled && (
                  <button
                    className="btn btn-success shadow-sm"
                    onClick={handleDownloadSettlementLetter}
                    disabled={isDownloadingLetter}
                    title="Letter confirming the loan is paid in full"
                  >
                    {isDownloadingLetter
                      ? <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      : <i className="fa fa-certificate me-2"></i>}
                    Settlement letter
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3" style={{ flex: 1, paddingBottom: '3rem', overflow: 'visible' }}>
        <div className="container-fluid" style={{ maxWidth: '100%' }}>
          {isSettled && (
            <div className="alert alert-success d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
              <div>
                <i className="fa fa-check-circle me-2"></i>
                <strong>Settled and closed</strong>
                {loanAccount?.closedDate ? <> on {formatDate(loanAccount.closedDate)}</> : null} ({settledHow}).
                <span className="ms-1">
                  Nothing more is owed, so payments, charges and discounts are closed. The history, statements and documents below stay available.
                </span>
              </div>
              <button className="btn btn-sm btn-success" onClick={handleDownloadSettlementLetter} disabled={isDownloadingLetter}>
                <i className="fa fa-download me-1"></i>Settlement letter
              </button>
            </div>
          )}

          {/* Summary Cards */}
          <div className="row g-4 mb-4">
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <p className="text-muted small mb-1">Total Outstanding</p>
                      <h3 className="mb-0 fw-bold text-danger">{formatCurrency(loanAccount.totalOutstanding || 0)}</h3>
                </div>
                    <div className="bg-danger bg-opacity-10 rounded-circle p-3">
                      <i className="fa fa-money fa-2x text-danger" style={{ display: 'block' }}></i>
                          </div>
                        </div>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center justify-content-between">
                        <div>
                      <p className="text-muted small mb-1">Disbursed Amount</p>
                      <h3 className="mb-0 fw-bold text-primary">{formatCurrency(loanAccount.disbursedAmount || 0)}</h3>
                        </div>
                    <div className="rounded-circle p-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
                      <i className="fa fa-arrow-up fa-2x text-primary" style={{ display: 'block' }}></i>
                      </div>
                    </div>
                          </div>
                        </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center justify-content-between">
                        <div>
                      <p className="text-muted small mb-1">Account Status</p>
                      <div className="mt-1">
                        <span className={`badge bg-${loanAccount.status === 'ACTIVE' ? 'success' : loanAccount.status === 'IN_ARREARS' ? 'warning' : loanAccount.status === 'DEFAULTED' ? 'danger' : 'secondary'} px-3 py-2`}>
                          {loanAccount.status}
                        </span>
                        </div>
                      </div>
                    <div className={`${loanAccount.status === 'ACTIVE' ? 'bg-success' : loanAccount.status === 'IN_ARREARS' ? 'bg-warning' : loanAccount.status === 'DEFAULTED' ? 'bg-danger' : 'bg-secondary'} bg-opacity-10 rounded-circle p-3`}>
                      <i className={`fa fa-${loanAccount.status === 'ACTIVE' ? 'check-circle' : loanAccount.status === 'IN_ARREARS' || loanAccount.status === 'DEFAULTED' ? 'exclamation-triangle' : 'circle'} fa-2x ${loanAccount.status === 'ACTIVE' ? 'text-success' : loanAccount.status === 'IN_ARREARS' ? 'text-warning' : loanAccount.status === 'DEFAULTED' ? 'text-danger' : 'text-secondary'}`} style={{ display: 'block' }}></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <p className="text-muted small mb-1">Payment History</p>
                      <h3 className="mb-0 fw-bold text-info">{paymentHistory.length}</h3>
                    </div>
                    <div className="bg-info bg-opacity-10 rounded-circle p-3">
                      <i className="fa fa-history fa-2x text-info" style={{ display: 'block' }}></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Account Information & Customer Details - Compact Design */}
          <div className="row g-3 mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-body p-3">
                  <div className="row g-3">
                    {/* Account Information Section */}
            <div className="col-lg-8">
                      <h6 className="mb-3 fw-bold d-flex align-items-center">
                        <i className="fa fa-info-circle text-primary me-2" style={{ fontSize: '1rem' }}></i>
                    Account Information
                      </h6>
                      <div className="row g-2">
                        <div className="col-md-4 col-sm-6">
                          <div className="d-flex align-items-center p-2 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                            <i className="fa fa-barcode text-primary me-2" style={{ fontSize: '0.875rem' }}></i>
                        <div className="flex-grow-1">
                              <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Account Number</small>
                              <div className="fw-semibold" style={{ fontSize: '0.875rem' }}>{loanAccount.accountNumber || loanAccount.accountId.substring(0, 8)}</div>
                  </div>
                      </div>
                    </div>
                        <div className="col-md-4 col-sm-6">
                          <div className="d-flex align-items-center p-2 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                            <i className="fa fa-cube text-success me-2" style={{ fontSize: '0.875rem' }}></i>
                        <div className="flex-grow-1">
                              <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Product Code</small>
                              <div className="fw-semibold" style={{ fontSize: '0.875rem' }}>
                                {loanAccount.installments && loanAccount.installments.length === 1
                                  ? 'Once off loan'
                                  : loanAccount.installments && loanAccount.installments.length > 1
                                    ? 'Monthly Loan'
                                    : loanAccount.productCode || 'N/A'}
                              </div>
                      </div>
                    </div>
                      </div>
                        <div className="col-md-4 col-sm-6">
                          <div className="d-flex align-items-center p-2 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                            <i className="fa fa-calendar text-info me-2" style={{ fontSize: '0.875rem' }}></i>
                        <div className="flex-grow-1">
                              <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Commencement Date</small>
                              <div className="fw-semibold" style={{ fontSize: '0.875rem' }}>{formatDate(loanAccount.disbursementDate || '')}</div>
                        </div>
                      </div>
                        </div>
                        <div className="col-md-4 col-sm-6">
                          <div className="d-flex align-items-center p-2 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                            <i className="fa fa-clock-o text-warning me-2" style={{ fontSize: '0.875rem' }}></i>
                        <div className="flex-grow-1">
                              <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Loan Term</small>
                              <div className="fw-semibold" style={{ fontSize: '0.875rem' }}>
                            {loanAccount.installments && loanAccount.installments.length === 1
                              ? '1 Month'
                              : loanAccount.installments && loanAccount.installments.length > 1
                                ? `${loanAccount.installments.length} Months`
                                : loanAccount.termMonths ? `${loanAccount.termMonths} Month${loanAccount.termMonths !== 1 ? 's' : ''}` : 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>
                        <div className="col-md-4 col-sm-6">
                          <div className="d-flex align-items-center p-2 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                            <i className="fa fa-calendar-check-o text-danger me-2" style={{ fontSize: '0.875rem' }}></i>
                        <div className="flex-grow-1">
                              <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Maturity Date</small>
                              <div className="fw-semibold" style={{ fontSize: '0.875rem' }}>{loanAccount.maturityDate ? formatDate(loanAccount.maturityDate) : 'N/A'}</div>
                        </div>
                      </div>
                    </div>
                        <div className="col-md-4 col-sm-6">
                          <div className="d-flex align-items-center p-2 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                            <i className="fa fa-calendar-plus-o text-primary me-2" style={{ fontSize: '0.875rem' }}></i>
                        <div className="flex-grow-1">
                              <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Due Date</small>
                              <div className="fw-semibold" style={{ fontSize: '0.875rem' }}>{getCurrentDueDate(loanAccount)}</div>
                    </div>
                  </div>
                      </div>
                    </div>
                      </div>

                    {/* Customer Details Section */}
            <div className="col-lg-4">
                      <h6 className="mb-3 fw-bold d-flex align-items-center">
                        <i className="fa fa-user text-primary me-2" style={{ fontSize: '1rem' }}></i>
                    Customer Details
                      </h6>
                      <div className="row g-2">
                        <div className="col-12">
                          <div className="d-flex align-items-center p-2 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                            <i className="fa fa-user text-primary me-2" style={{ fontSize: '0.875rem' }}></i>
                            <div className="flex-grow-1">
                              <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Customer Name</small>
                              <div className="fw-semibold" style={{ fontSize: '0.875rem' }}>{loanAccount.customerName || 'N/A'}</div>
                    </div>
                      </div>
                    </div>
                        <div className="col-12">
                          <div className="d-flex align-items-center p-2 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                            <i className="fa fa-id-card text-info me-2" style={{ fontSize: '0.875rem' }}></i>
                      <div className="flex-grow-1">
                              <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Customer Number</small>
                              <div className="fw-semibold" style={{ fontSize: '0.875rem' }}>{loanAccount.customerNumber || 'N/A'}</div>
                  </div>
                    </div>
                  </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Installments Table */}
          {loanAccount.installments && loanAccount.installments.length > 0 && (
            <div className="row g-4 mb-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white border-0 py-3 px-4">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0 fw-bold d-flex align-items-center">
                        <i className="fa fa-calendar-check-o text-info me-2"></i>
                        Repayment Schedule
                      </h5>
                    </div>
                  </div>
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <DataTable
                        columns={[
                          {
                            name: 'Seq',
                            selector: (row: LedgerInstallment) => row.sequenceNum || 0,
                            sortable: true,
                            cell: (row: LedgerInstallment) => (
                              <div className="d-flex align-items-center justify-content-center">
                                <span className="fw-bold text-muted">#{row.sequenceNum || 0}</span>
                              </div>
                            ),
                            minWidth: '70px',
                            center: true,
                          },
                          // No Due Date column: the business asked for it to go (finding #24); the
                          // loan's agreed Due Date is shown above the schedule.
                          {
                            name: 'Principal',
                            selector: (row: LedgerInstallment) => row.principalDue || 0,
                            sortable: true,
                            cell: (row: LedgerInstallment) => (
                              <div className="text-end fw-semibold">{formatCurrency(row.principalDue || 0)}</div>
                            ),
                            minWidth: '130px',
                            right: true,
                          },
                          {
                            name: 'Interest',
                            selector: (row: LedgerInstallment) => row.interestDue || 0,
                            sortable: true,
                            cell: (row: LedgerInstallment) => (
                              <div className="text-end fw-semibold">{formatCurrency(row.interestDue || 0)}</div>
                            ),
                            minWidth: '130px',
                            right: true,
                          },
                          {
                            name: 'Fees',
                            selector: (row: LedgerInstallment) => row.feesDue || 0,
                            sortable: true,
                            cell: (row: LedgerInstallment) => (
                              <div className="text-end fw-semibold">{formatCurrency(row.feesDue || 0)}</div>
                            ),
                            minWidth: '120px',
                            right: true,
                          },
                          {
                            name: 'Total Due',
                            selector: (row: LedgerInstallment) => installmentRemaining(row),
                            sortable: true,
                            cell: (row: LedgerInstallment) => {
                              const charges = installmentChargesOutstanding(row);
                              const scheduled = (row.principalDue || 0) + (row.interestDue || 0) + (row.feesDue || 0) + (row.penaltiesDue || 0);
                              const remaining = installmentRemaining(row);
                              return (
                                <div className="text-end">
                                  <div className={`fw-bold ${remaining > 0 ? 'text-primary' : 'text-success'}`}>{formatCurrency(remaining)}</div>
                                  {remaining !== scheduled && (
                                    <div className="text-muted small">of {formatCurrency(scheduled)}</div>
                                  )}
                                  {charges > 0 && (
                                    <div className="small text-danger" title="Default charges raised against this instalment and not yet paid">
                                      incl. {formatCurrency(charges)} charges
                                    </div>
                                  )}
                                </div>
                              );
                            },
                            minWidth: '130px',
                            right: true,
                          },
                          {
                            name: 'Principal Paid',
                            selector: (row: LedgerInstallment) => row.principalPaid || 0,
                            sortable: true,
                            cell: (row: LedgerInstallment) => (
                              <div className="text-end text-success">{formatCurrency(row.principalPaid || 0)}</div>
                            ),
                            minWidth: '130px',
                            right: true,
                          },
                          {
                            name: 'Interest Paid',
                            selector: (row: LedgerInstallment) => row.interestPaid || 0,
                            sortable: true,
                            cell: (row: LedgerInstallment) => (
                              <div className="text-end text-success">{formatCurrency(row.interestPaid || 0)}</div>
                            ),
                            minWidth: '130px',
                            right: true,
                          },
                          {
                            name: 'Fees Paid',
                            selector: (row: LedgerInstallment) => row.feesPaid || 0,
                            sortable: true,
                            cell: (row: LedgerInstallment) => (
                              <div className="text-end text-success">{formatCurrency(row.feesPaid || 0)}</div>
                            ),
                            minWidth: '120px',
                            right: true,
                          },
                          {
                            name: 'Status',
                            selector: (row: LedgerInstallment) => row.status || '',
                            sortable: true,
                            cell: (row: LedgerInstallment) => {
                              const getStatusColor = (status?: string) => {
                                switch (status) {
                                  case 'PAID':
                                    return { bg: 'success', icon: 'fa-check-circle', text: 'Paid' };
                                  case 'DUE':
                                    return { bg: 'warning', icon: 'fa-clock', text: 'Due' };
                                  case 'PARTIALLY_PAID':
                                    return { bg: 'warning', icon: 'fa-adjust', text: 'Partially Paid' };
                                  case 'OVERDUE':
                                    return { bg: 'danger', icon: 'fa-exclamation-triangle', text: 'Overdue' };
                                  case 'PENDING':
                                    return { bg: 'info', icon: 'fa-calendar', text: 'Pending' };
                                  default:
                                    return { bg: 'secondary', icon: 'fa-circle', text: 'Unknown' };
                                }
                              };
                              const statusInfo = getStatusColor(row.status);
                              return (
                                <div className="d-flex justify-content-center">
                                  <span className={`badge bg-${statusInfo.bg} px-3 py-2 d-inline-flex align-items-center gap-1`}>
                                    <i className={`fa ${statusInfo.icon}`}></i>
                                    {statusInfo.text}
                                  </span>
                                </div>
                              );
                            },
                            minWidth: '120px',
                            center: true,
                          },
                          {
                            name: 'Actions',
                            cell: (row: LedgerInstallment) => <InstallmentActions installment={row} />,
                            minWidth: '100px',
                            center: true,
                          },
                        ]}
                        data={loanAccount.installments}
                        pagination
                        highlightOnHover
                        striped
                        responsive
                        paginationPerPage={10}
                        paginationRowsPerPageOptions={[10, 25, 50]}
                        customStyles={{
                          table: {
                            style: {
                              backgroundColor: 'white',
                            },
                          },
                          headRow: {
                            style: {
                              backgroundColor: '#f8f9fa',
                              borderBottom: '2px solid #dee2e6',
                              borderRadius: '0',
                            },
                          },
                          headCells: {
                            style: {
                              fontWeight: 600,
                              fontSize: '0.8rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              paddingTop: '1rem',
                              paddingBottom: '1rem',
                              paddingLeft: '1rem',
                              paddingRight: '1rem',
                              color: '#495057',
                              borderRight: '1px solid #e9ecef',
                              whiteSpace: 'normal',
                              wordWrap: 'break-word',
                              lineHeight: '1.4',
                            },
                          },
                          cells: {
                            style: {
                              paddingTop: '1rem',
                              paddingBottom: '1rem',
                              paddingLeft: '1rem',
                              paddingRight: '1rem',
                              borderRight: '1px solid #f8f9fa',
                              fontSize: '0.875rem',
                            },
                          },
                          rows: {
                            style: {
                              minHeight: '60px',
                              borderBottom: '1px solid #f0f0f0',
                              '&:hover': {
                                backgroundColor: '#f8f9fa',
                                cursor: 'default',
                              },
                            },
                            stripedStyle: {
                              backgroundColor: '#ffffff',
                            },
                          },
                          pagination: {
                            style: {
                              backgroundColor: '#f8f9fa',
                              borderTop: '1px solid #dee2e6',
                              padding: '1rem',
                            },
                          },
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Payment History */}
          <div className="row g-4 mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-0 py-3 px-4">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 fw-bold d-flex align-items-center">
                      <i className="fa fa-history text-primary me-2"></i>
                      Payment History
                    </h5>
                </div>
                </div>
                <div className="card-body p-0">
                  {isLoadingPayments ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-3 text-muted">Loading payment history...</p>
                    </div>
                  ) : paymentHistory.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="fa fa-inbox fa-3x text-muted mb-3"></i>
                      <h5 className="text-muted">No Payment History</h5>
                      <p className="text-muted mb-0">There are no payment transactions for this account yet.</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <DataTable
                        columns={paymentColumns}
                        data={paymentHistory}
                        pagination
                        highlightOnHover
                        striped
                        responsive
                        paginationPerPage={10}
                        paginationRowsPerPageOptions={[10, 25, 50]}
                        customStyles={{
                          table: {
                            style: {
                              backgroundColor: 'white',
                            },
                          },
                          headRow: {
                            style: {
                              backgroundColor: '#f8f9fa',
                              borderBottom: '2px solid #dee2e6',
                              borderRadius: '0',
                            },
                          },
                          headCells: {
                            style: {
                              fontWeight: 700,
                              fontSize: '0.875rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              paddingTop: '1.25rem',
                              paddingBottom: '1.25rem',
                              paddingLeft: '1.5rem',
                              paddingRight: '1.5rem',
                              color: '#495057',
                              borderRight: '1px solid #e9ecef',
                            },
                          },
                          cells: {
                            style: {
                              paddingTop: '1.25rem',
                              paddingBottom: '1.25rem',
                              paddingLeft: '1.5rem',
                              paddingRight: '1.5rem',
                              borderRight: '1px solid #f8f9fa',
                            },
                          },
                          rows: {
                            style: {
                              minHeight: '70px',
                              borderBottom: '1px solid #f8f9fa',
                              '&:hover': {
                                backgroundColor: '#f8f9fa',
                                cursor: 'pointer',
                              },
                            },
                            stripedStyle: {
                              backgroundColor: '#ffffff',
                            },
                          },
                          pagination: {
                            style: {
                              backgroundColor: '#f8f9fa',
                              borderTop: '1px solid #dee2e6',
                              padding: '1rem',
                            },
                          },
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Audit Trail - Button to open modal */}
          <div className="row g-4 mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-body p-4 d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle p-3 me-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(108,117,125,0.2)' }}>
                      <i className="fa fa-list-alt fa-lg text-secondary" style={{ display: 'block' }}></i>
                    </div>
                    <div>
                      <h6 className="mb-0 fw-bold">Audit Trail</h6>
                      <small className="text-muted">View full transaction history including rectified payments</small>
                    </div>
                  </div>
                  <button
                    className="btn btn-outline-secondary rounded-pill px-4"
                    onClick={() => setShowAuditTrailModal(true)}
                    disabled={isLoadingPayments || allTransactions.length === 0}
                  >
                    <i className="fa fa-eye me-2"></i>
                    View Audit Trail
                    {allTransactions.length > 0 && (
                      <span className="badge bg-secondary ms-2">{allTransactions.length}</span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Penalty History */}
          <div className="row g-4 mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-0 py-3 px-4">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 fw-bold d-flex align-items-center">
                      <i className="fa fa-file-text text-warning me-2"></i>
                      Charge History
                      {penaltyHistory && penaltyHistory.length > 0 && (
                        <span className="badge bg-warning ms-2">{penaltyHistory.length}</span>
                      )}
                    </h5>
                </div>
                </div>
                <div className="card-body p-0">
                  {isLoadingPenaltyHistory ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2">Loading charge history...</p>
                    </div>
                  ) : !penaltyHistory || penaltyHistory.length === 0 ? (
                    <div className="text-center py-5">
                      <div className="mb-3">
                        <i className="fa fa-history text-muted fa-3x"></i>
                      </div>
                      <h5 className="text-muted">No Charge History</h5>
                      <p className="text-muted mb-0">There is no charge history for this loan account.</p>
                    </div>
                  ) : (
                    <div style={{ width: '100%', overflowX: 'auto' }}>
                    <DataTable
                      columns={penaltyHistoryColumns}
                      data={penaltyHistory}
                      pagination
                      highlightOnHover
                      striped
                      responsive
                      paginationPerPage={10}
                      paginationRowsPerPageOptions={[10, 25, 50]}
                      customStyles={{
                        table: {
                          style: {
                            backgroundColor: 'white',
                          },
                        },
                        headRow: {
                          style: {
                            backgroundColor: '#f8f9fa',
                            borderBottom: '2px solid #dee2e6',
                            borderRadius: '0',
                          },
                        },
                        headCells: {
                          style: {
                            fontWeight: 700,
                            fontSize: '0.875rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            paddingTop: '1.25rem',
                            paddingBottom: '1.25rem',
                            paddingLeft: '1.5rem',
                            paddingRight: '1.5rem',
                            color: '#495057',
                            borderRight: '1px solid #e9ecef',
                          },
                        },
                        cells: {
                          style: {
                            paddingTop: '1.25rem',
                            paddingBottom: '1.25rem',
                            paddingLeft: '1.5rem',
                            paddingRight: '1.5rem',
                            borderRight: '1px solid #f8f9fa',
                          },
                        },
                        rows: {
                          style: {
                            minHeight: '70px',
                            borderBottom: '1px solid #f8f9fa',
                            '&:hover': {
                              backgroundColor: '#f8f9fa',
                              cursor: 'pointer',
                            },
                          },
                          stripedStyle: {
                            backgroundColor: '#ffffff',
                          },
                        },
                        pagination: {
                          style: {
                            backgroundColor: '#f8f9fa',
                            borderTop: '1px solid #dee2e6',
                            padding: '1rem',
                          },
                        },
                      }}
                    />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Pending Penalties */}
          <div className="row g-4 mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-0 py-3 px-4">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 fw-bold d-flex align-items-center">
                      <i className="fa fa-exclamation-triangle text-danger me-2"></i>
                      Pending Charges
                      {pendingPenalties.length > 0 && (
                        <span className="badge bg-danger ms-2">{pendingPenalties.length}</span>
                      )}
                    </h5>
                </div>
                </div>
                <div className="card-body p-0">
                  {isLoadingPendingPenalties ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2">Loading charges...</p>
                    </div>
                  ) : pendingPenalties.length === 0 ? (
                    <div className="text-center py-5">
                      <div className="mb-3">
                        <i className="fa fa-check-circle text-success fa-3x"></i>
                      </div>
                      <h5 className="text-muted">No Pending Charges</h5>
                      <p className="text-muted mb-0">There are currently no pending charges for this loan account.</p>
                    </div>
                  ) : (
                    <div style={{ width: '100%', overflowX: 'auto' }}>
                    <DataTable
                      columns={penaltyColumns}
                      data={pendingPenalties}
                      pagination
                      highlightOnHover
                      striped
                      responsive
                      paginationPerPage={10}
                      paginationRowsPerPageOptions={[10, 25, 50]}
                      customStyles={{
                        table: {
                          style: {
                            backgroundColor: 'white',
                          },
                        },
                        headRow: {
                          style: {
                            backgroundColor: '#f8f9fa',
                            borderBottom: '2px solid #dee2e6',
                            borderRadius: '0',
                          },
                        },
                        headCells: {
                          style: {
                            fontWeight: 700,
                            fontSize: '0.875rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            paddingTop: '1.25rem',
                            paddingBottom: '1.25rem',
                            paddingLeft: '1.5rem',
                            paddingRight: '1.5rem',
                            color: '#495057',
                            borderRight: '1px solid #e9ecef',
                          },
                        },
                        cells: {
                          style: {
                            paddingTop: '1.25rem',
                            paddingBottom: '1.25rem',
                            paddingLeft: '1.5rem',
                            paddingRight: '1.5rem',
                            borderRight: '1px solid #f8f9fa',
                          },
                        },
                        rows: {
                          style: {
                            minHeight: '70px',
                            borderBottom: '1px solid #f8f9fa',
                            '&:hover': {
                              backgroundColor: '#f8f9fa',
                              cursor: 'pointer',
                            },
                          },
                          stripedStyle: {
                            backgroundColor: '#ffffff',
                          },
                        },
                        pagination: {
                          style: {
                            backgroundColor: '#f8f9fa',
                            borderTop: '1px solid #dee2e6',
                            padding: '1rem',
                          },
                        },
                      }}
                    />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Collection Notes Section: contacts with the client and promises to pay (US-36) */}
      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1" id="follow-up">
        <div className="container-fluid">
          <div className="row g-4 mb-4">
            <div className="col-12">
              {loanAccount.accountId && (
                <CollectionNotes
                  accountId={loanAccount.accountId}
                  className="mb-4"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* How the loan was decided and what the customer signed, kept with the loan itself. */}
      {loanAccount.applicationId && (
        <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1">
          <div className="container-fluid">
            <div className="row g-4">
              <div className="col-12 col-lg-7">
                <CreditAssessmentSummary applicationId={loanAccount.applicationId} />
                {/* Conditions set to be met after disbursement are tracked here, on the loan itself. */}
                <ApprovalConditionsCard applicationId={loanAccount.applicationId} />
              </div>
              <div className="col-12 col-lg-5">
                <LoanAgreementsCard applicationId={loanAccount.applicationId} />
                <DisbursementsCard applicationId={loanAccount.applicationId} />
                <ReverseDiscountModal
                  discount={reverseDiscountTarget}
                  onHide={() => setReverseDiscountTarget(null)}
                  onReversed={() => {
                    fetchLoanAccount();
                    fetchPaymentHistory();
                  }}
                />
                <LoanStatementModal
                  accountId={loanAccount.accountId}
                  accountNumber={loanAccount.accountNumber}
                  disbursementDate={loanAccount.disbursementDate}
                  show={showStatementModal}
                  onHide={() => setShowStatementModal(false)}
                />
                <DiscountWaiverModal
                  accountId={loanAccount.accountId}
                  show={showDiscountModal}
                  onHide={() => setShowDiscountModal(false)}
                  onRecorded={() => {
                    fetchLoanAccount();
                    fetchPaymentHistory();
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loan Application Comments Section */}
      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1">
        <div className="container-fluid">
          <div className="row g-4 mb-4">
            <div className="col-12">
              {loanAccount.applicationId ? (
                <LoanApplicationComments
                  loanApplicationId={loanAccount.applicationId}
                  currentStage={getLoanApplicationStage(loanAccount.status)}
                  className="mb-4"
                />
              ) : (
                <div className="alert alert-warning mb-0">
                  <i className="fa fa-exclamation-triangle me-2"></i>
                  Unable to load comments: this account's application ID is missing from the backend response.
                  <br /><small className="text-muted">Account ID: {loanAccount.accountId}</small>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Reallocate & Rectify Modals */}
      <Portal>
        {/* Payment Modal */}
        <div className="modal fade modal-slide-in-left" id="LoanAccountPayment" tabIndex={-1} aria-labelledby="LoanAccountPaymentLabel" aria-hidden="true">
          <div className="modal-dialog modal-dialog-vertical modal-dialog-scrollable" style={{ maxWidth: '900px', width: '90%' }}>
            <div className="modal-content rounded-0 border-0 shadow-lg">
              <div className="modal-header border-bottom pb-0" style={{ padding: '2rem 2rem 1.5rem 2rem', backgroundColor: '#f8f9fa' }}>
                <div className="w-100">
                  <div className="d-flex align-items-center justify-content-between mb-4">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle p-3 me-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
                        <i className="fa fa-credit-card fa-2x text-primary" style={{ display: 'block' }}></i>
                      </div>
                      <div>
                        <h4 className="modal-title mb-0 text-dark fw-bold" id="LoanAccountPaymentLabel">Process Payment</h4>
                        <p className="text-muted mb-0 mt-1 small">Record payment transaction for this account</p>
                      </div>
                    </div>
                    <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                  </div>
                  <div className="row g-3">
                    <div className="col-6">
                      <div className="rounded-3 p-3 border bg-white" style={{ borderColor: '#e9ecef', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                        <div className="d-flex align-items-center">
                          <div className="rounded-circle p-2 me-2" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
                            <i className="fa fa-user text-primary" style={{ display: 'block' }}></i>
                          </div>
                          <div className="flex-grow-1">
                            <div className="text-muted small">Customer</div>
                            <div className="text-dark fw-semibold small">{loanAccount?.customerNumber || 'N/A'}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="rounded-3 p-3 border bg-white" style={{ borderColor: '#e9ecef', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                        <div className="d-flex align-items-center">
                          <div className="rounded-circle p-2 me-2" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(255,193,7,0.2)' }}>
                            <i className="fa fa-money text-warning" style={{ display: 'block' }}></i>
                          </div>
                          <div className="flex-grow-1">
                            <div className="text-muted small">Outstanding</div>
                            <div className="text-danger fw-bold">{formatCurrency(loanAccount?.totalOutstanding || 0)}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-body custom_scroll p-4" style={{ backgroundColor: '#f8f9fa', minHeight: 0 }}>
                <FormProvider {...methods}>
                  <form onSubmit={handleSubmit(handleProcessPayment)}>
                    <div className="card border-0 shadow-sm mb-4">
                      <div className="card-body p-4">
                        <div className="d-flex align-items-center mb-4">
                          <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
                            <i className="fa fa-wallet text-primary" style={{ display: 'block' }}></i>
                          </div>
                          <div>
                            <h6 className="mb-0 fw-bold">Payment Details</h6>
                            <small className="text-muted">Enter the payment amount and date</small>
                          </div>
                        </div>
                        <div className="row g-4">
                          <div className="col-md-6">
                            <label htmlFor="paymentAmount" className="form-label fw-semibold mb-2">Payment Amount <span className="text-danger">*</span></label>
                            <div className="input-group input-group-lg" style={{ borderRadius: '0.5rem', overflow: 'hidden', border: errors.paymentAmount ? '2px solid #dc3545' : '1px solid #dee2e6' }}>
                              <span className="input-group-text bg-white border-0 px-4">
                                <i className="fa fa-credit-card text-primary fa-lg" style={{ display: 'block' }}></i>
                              </span>
                              <input
                                type="number"
                                id="paymentAmount"
                                className={`form-control border-0 ${errors.paymentAmount ? 'is-invalid' : ''}`}
                                placeholder="0.00"
                                style={{ fontSize: '1.1rem', fontWeight: 500 }}
                                {...register('paymentAmount')}
                                min="0.01"
                                step="0.01"
                              />
                            </div>
                            {errors.paymentAmount && (
                              <div className="text-danger small mt-2">
                                <i className="fa fa-exclamation-circle me-1"></i>{errors.paymentAmount.message}
                              </div>
                            )}
                            {loanAccount?.totalOutstanding && (
                              <div className="mt-2 p-2 rounded" style={{ backgroundColor: '#e7f3ff', border: '1px solid #b3d9ff' }}>
                                <small className="text-primary d-flex align-items-center">
                                  <i className="fa fa-info-circle me-2"></i>
                                  <span>Outstanding balance: <strong>{formatCurrency(loanAccount.totalOutstanding)}</strong></span>
                                </small>
                              </div>
                            )}
                          </div>
                          <div className="col-md-6">
                            <label htmlFor="installmentAmount" className="form-label fw-semibold mb-2">Installment Amount</label>
                            <div className="input-group input-group-lg" style={{ borderRadius: '0.5rem', overflow: 'hidden', border: '1px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
                              <span className="input-group-text bg-white border-0 px-4" style={{ backgroundColor: '#f8f9fa' }}>
                                <i className="fa fa-calendar-check-o text-info fa-lg" style={{ display: 'block' }}></i>
                              </span>
                              <input
                                type="text"
                                id="installmentAmount"
                                className="form-control border-0 bg-light"
                                style={{ fontSize: '1.1rem', fontWeight: 500 }}
                                value={formatCurrency(getInstallmentAmount(loanAccount))}
                                readOnly
                                disabled
                              />
                            </div>
                            <small className="text-muted d-block mt-2">
                              <i className="fa fa-info-circle me-1"></i>Next installment amount for reference
                            </small>
                          </div>
                          <div className="col-md-6">
                            <label htmlFor="valueDate" className="form-label fw-semibold mb-2">Payment Date <span className="text-danger">*</span></label>
                            <div className="input-group input-group-lg" style={{ borderRadius: '0.5rem', overflow: 'hidden', border: errors.valueDate ? '2px solid #dc3545' : '1px solid #dee2e6' }}>
                              <span className="input-group-text bg-white border-0 px-4">
                                <i className="fa fa-calendar text-primary fa-lg" style={{ display: 'block' }}></i>
                              </span>
                              <input
                                type="date"
                                id="valueDate"
                                className={`form-control border-0 ${errors.valueDate ? 'is-invalid' : ''}`}
                                style={{ fontSize: '1.1rem', fontWeight: 500 }}
                                {...register('valueDate')}
                              />
                            </div>
                            {errors.valueDate && (
                              <div className="text-danger small mt-2">
                                <i className="fa fa-exclamation-circle me-1"></i>{errors.valueDate.message}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="card border-0 shadow-sm mb-4">
                      <div className="card-body p-4">
                        <div className="d-flex align-items-center mb-4">
                          <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(108,117,125,0.2)' }}>
                            <i className="fa fa-file-text text-secondary" style={{ display: 'block' }}></i>
                          </div>
                          <div>
                            <h6 className="mb-0 fw-bold">Additional Information</h6>
                            <small className="text-muted">Optional details for tracking</small>
                          </div>
                        </div>
                        <div className="row g-4">
                          <div className="col-md-6">
                            <label htmlFor="externalRef" className="form-label fw-semibold mb-2">Bank Reference</label>
                            <div className="input-group" style={{ borderRadius: '0.5rem', overflow: 'hidden', border: '1px solid #dee2e6' }}>
                              <span className="input-group-text bg-white border-0 px-4">
                                <i className="fa fa-tag text-secondary" style={{ display: 'block' }}></i>
                              </span>
                              <input
                                type="text"
                                id="externalRef"
                                className="form-control border-0"
                                placeholder="e.g., Transaction ID or Reference"
                                {...register('externalRef')}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <label htmlFor="remarks" className="form-label fw-semibold mb-2">Remarks</label>
                            <div className="input-group" style={{ borderRadius: '0.5rem', overflow: 'hidden', border: '1px solid #dee2e6' }}>
                              <span className="input-group-text bg-white border-0 px-4">
                                <i className="fa fa-comment text-secondary" style={{ display: 'block' }}></i>
                              </span>
                              <input
                                type="text"
                                id="remarks"
                                className="form-control border-0"
                                placeholder="Add any notes or comments"
                                {...register('remarks')}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex justify-content-end gap-3 mt-4 pt-3 border-top position-relative">
                      {(isProcessingPayment || recordPaymentMutation.isPending) && (
                        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: 'rgba(255,255,255,0.8)', zIndex: 10, borderRadius: '0.5rem' }}>
                          <div className="text-center">
                            <div className="spinner-border text-success mb-2" role="status" style={{ width: '3rem', height: '3rem' }}>
                              <span className="visually-hidden">Processing...</span>
                            </div>
                            <div className="fw-semibold text-dark">Processing Payment...</div>
                          </div>
                        </div>
                      )}
                      <button type="button" className="btn btn-outline-secondary px-5 py-2 rounded-pill" data-bs-dismiss="modal" disabled={isProcessingPayment || recordPaymentMutation.isPending}>
                        <i className="fa fa-times me-2"></i>Cancel
                      </button>
                      <button type="submit" className="btn btn-success px-5 py-2 rounded-pill shadow-sm fw-semibold" disabled={isProcessingPayment || recordPaymentMutation.isPending} style={{ minWidth: '180px' }}>
                        {(isProcessingPayment || recordPaymentMutation.isPending) ? (
                          <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Processing...</>
                        ) : (
                          <><i className="fa fa-check-circle me-2"></i>Process Payment</>
                        )}
                      </button>
                    </div>
                  </form>
                </FormProvider>
              </div>
            </div>
          </div>
        </div>

        {/* Reallocate Funds Modal */}
        <div className="modal fade modal-slide-in-left" id="ReallocateFundsModal" tabIndex={-1} aria-labelledby="ReallocateFundsModalLabel" aria-hidden="true">
          <div className="modal-dialog modal-dialog-vertical modal-dialog-scrollable" style={{ maxWidth: '900px', width: '90%' }}>
            <div className="modal-content rounded-0 border-0 shadow-lg">
              <div className="modal-header border-bottom pb-0" style={{ padding: '2rem 2rem 1.5rem 2rem', backgroundColor: '#f8f9fa' }}>
                <div className="w-100 d-flex align-items-center justify-content-between">
                  <div>
                    <h4 className="modal-title mb-0 text-dark fw-bold" id="ReallocateFundsModalLabel">Re-allocate Funds</h4>
                    <p className="text-muted mb-0 mt-1 small">Move the selected payment amount to a different loan account</p>
                  </div>
                  <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
              </div>
              <div className="modal-body custom_scroll p-4" style={{ backgroundColor: '#f8f9fa' }}>
                <form onSubmit={handleReallocateFundsSubmit}>
                  {/* From (Source) Info */}
                  <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body p-3">
                      <div className="d-flex align-items-center">
                        <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#f8d7da', border: '1px solid rgba(220,53,69,0.2)' }}>
                          <i className="fa fa-sign-out text-danger" style={{ display: 'block' }}></i>
                        </div>
                        <div>
                          <small className="text-muted d-block">From</small>
                          <span className="fw-semibold text-dark">
                            {loanAccount?.customerName || 'N/A'}
                          </span>
                          <span className="text-muted ms-2 small">
                            ({loanAccount?.accountNumber || loanAccount?.accountId || 'N/A'})
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Arrow / To indicator */}
                  <div className="text-center my-2">
                    <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill" style={{ backgroundColor: '#e9ecef' }}>
                      <i className="fa fa-long-arrow-down text-primary"></i>
                      <span className="fw-semibold text-primary small">To</span>
                      <i className="fa fa-long-arrow-down text-primary"></i>
                    </div>
                  </div>

                  {/* To (Target) Info - shown when company is selected */}
                  {selectedCompany && (
                    <div className="card border-0 shadow-sm mb-3">
                      <div className="card-body p-3">
                        <div className="d-flex align-items-center">
                          <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#d4edda', border: '1px solid rgba(40,167,69,0.2)' }}>
                            <i className="fa fa-sign-in text-success" style={{ display: 'block' }}></i>
                          </div>
                          <div>
                            <small className="text-muted d-block">To</small>
                            <span className="fw-semibold text-dark">
                              {getCustomerDisplayName(selectedCompany)}
                            </span>
                            {reallocateFundsForm.targetAccountId && (
                              <span className="text-muted ms-2 small">
                                ({Array.isArray(targetAccounts) ? getAccountDisplay(targetAccounts.find((a: any) => a.accountId === reallocateFundsForm.targetAccountId) || targetAccounts[0]) : ''})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="card border-0 shadow-sm mb-4">
                    <div className="card-body p-4">
                                            <div className="row g-4">
                                                <div className="col-md-6 position-relative">
                          <label className="form-label fw-semibold mb-2">Company <span className="text-danger">*</span></label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search company by name (min 2 chars)"
                            value={companyQuery}
                            onChange={(e) => {
                              setCompanyQuery(e.target.value);
                              setSelectedCompany(null);
                              setReallocateFundsForm((cur) => ({ ...cur, targetCustomerId: '', targetAccountId: '' }));
                            }}
                          />
                          {/* Suggestions */}
                          {companyQuery.length >= 2 && (
                            <div className="border rounded mt-1 bg-white shadow-sm" style={{ maxHeight: '200px', overflowY: 'auto', position: 'absolute', zIndex: 1070, width: '100%' }}>
                              {isSearchingCompanies && (
                                <div className="p-2 text-muted small">Searching...</div>
                              )}
                              {!isSearchingCompanies && searchResults?.content?.length === 0 && (
                                <div className="p-2 text-muted small">No companies found</div>
                              )}
                              {!isSearchingCompanies && (searchResults?.content || []).filter((c: any) => c.type === 'CORPORATE').map((c: any) => (
                                <button
                                  type="button"
                                  key={c.id}
                                  className="dropdown-item text-wrap"
                                  onClick={() => {
                                    setSelectedCompany(c);
                                    setCompanyQuery(getCustomerDisplayName(c));
                                    setReallocateFundsForm((cur) => ({ ...cur, targetCustomerId: c.id, targetAccountId: '' }));
                                  }}
                                >
                                  {getCustomerDisplayName(c)} {c.customerNumber ? `(${c.customerNumber})` : ''}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-semibold mb-2">Target Account <span className="text-danger">*</span></label>
                          {selectedCompany && isLoadingTargetAccounts && (
                            <div className="form-control bg-light text-muted">Loading accounts...</div>
                          )}
                          {!selectedCompany && (
                            <input className="form-control" value="Select a company first" readOnly />
                          )}
                          {selectedCompany && !isLoadingTargetAccounts && Array.isArray(targetAccounts) && targetAccounts.length > 1 && (
                            <select
                              className="form-select"
                              value={reallocateFundsForm.targetAccountId}
                              onChange={(e) => setReallocateFundsForm((cur) => ({ ...cur, targetAccountId: e.target.value }))}
                            >
                              <option value="">Select target account</option>
                              {targetAccounts.map((a: any) => (
                                <option key={a.accountId} value={a.accountId}>
                                  {getAccountDisplay(a)}
                                </option>
                              ))}
                            </select>
                          )}
                          {selectedCompany && !isLoadingTargetAccounts && Array.isArray(targetAccounts) && targetAccounts.length === 1 && (
                            <div className="input-group">
                              <span className="input-group-text"><i className="fa fa-credit-card"></i></span>
                              <input className="form-control" value={getAccountDisplay(targetAccounts[0])} readOnly />
                            </div>
                          )}
                          
                          {selectedCompany && !isLoadingTargetAccounts && Array.isArray(targetAccounts) && targetAccounts.length === 0 && (
                            <div className="alert alert-warning py-2 mb-0">No accounts found for this company.</div>
                          )}
                        </div>

                        <div className="col-md-6">
                          <label className="form-label fw-semibold mb-2">Amount (ZAR) <span className="text-danger">*</span></label>
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            className="form-control"
                            value={reallocateFundsForm.amount}
                            onChange={(event) => setReallocateFundsForm((current) => ({ ...current, amount: event.target.value }))}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-semibold mb-2">Value Date <span className="text-danger">*</span></label>
                          <input
                            type="date"
                            className="form-control"
                            value={reallocateFundsForm.valueDate}
                            onChange={(event) => setReallocateFundsForm((current) => ({ ...current, valueDate: event.target.value }))}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-semibold mb-2">Remarks</label>
                          <input
                            type="text"
                            className="form-control"
                            value={reallocateFundsForm.remarks}
                            onChange={(event) => setReallocateFundsForm((current) => ({ ...current, remarks: event.target.value }))}
                            placeholder="Optional notes"
                          />
                        </div>
                      </div>

                    </div>
                  </div>
                  <div className="d-flex justify-content-end gap-3 mt-4 pt-3 border-top">
                    <button type="button" className="btn btn-outline-secondary px-5 py-2 rounded-pill" data-bs-dismiss="modal">
                      <i className="fa fa-times me-2"></i>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-info px-5 py-2 rounded-pill shadow-sm fw-semibold" disabled={isProcessingReallocate}>
                      {isProcessingReallocate ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Re-allocating...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-check-circle me-2"></i>
                          Re-allocate Funds
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Rectify Payment Modal */}
        <div className="modal fade modal-slide-in-left" id="RectifyLoanModal" tabIndex={-1} aria-labelledby="RectifyLoanModalLabel" aria-hidden="true">
          <div className="modal-dialog modal-dialog-vertical modal-dialog-scrollable" style={{ maxWidth: '900px', width: '90%' }}>
            <div className="modal-content rounded-0 border-0 shadow-lg">
              <div className="modal-header border-bottom pb-0" style={{ padding: '2rem 2rem 1.5rem 2rem', backgroundColor: '#f8f9fa' }}>
                <div className="w-100 d-flex align-items-center justify-content-between">
                  <div>
                    <h4 className="modal-title mb-0 text-dark fw-bold" id="RectifyLoanModalLabel">Rectify Payment</h4>
                    <p className="text-muted mb-0 mt-1 small">Correct payment details for the selected loan transaction</p>
                  </div>
                  <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
              </div>
              <div className="modal-body custom_scroll p-4" style={{ backgroundColor: '#f8f9fa' }}>
                <form onSubmit={handleRectifyLoanSubmit}>
                  <div className="card border-0 shadow-sm mb-4">
                    <div className="card-body p-4">
                      <div className="row g-4">
                        <div className="col-md-6">
                          <label className="form-label fw-semibold mb-2">Correct Amount (ZAR) <span className="text-danger">*</span></label>
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            className="form-control"
                            value={rectifyLoanForm.correctAmount}
                            onChange={(event) => setRectifyLoanForm((current) => ({ ...current, correctAmount: event.target.value }))}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-semibold mb-2">Correct Value Date <span className="text-danger">*</span></label>
                          <input
                            type="date"
                            className="form-control"
                            value={rectifyLoanForm.correctValueDate}
                            onChange={(event) => setRectifyLoanForm((current) => ({ ...current, correctValueDate: event.target.value }))}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-semibold mb-2">Bank Reference</label>
                          <input
                            type="text"
                            className="form-control"
                            value={rectifyLoanForm.externalRef}
                            onChange={(event) => setRectifyLoanForm((current) => ({ ...current, externalRef: event.target.value }))}
                            placeholder="e.g., bank transaction reference"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex justify-content-end gap-3 mt-4 pt-3 border-top">
                    <button type="button" className="btn btn-outline-secondary px-5 py-2 rounded-pill" data-bs-dismiss="modal">
                      <i className="fa fa-times me-2"></i>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-warning px-5 py-2 rounded-pill shadow-sm fw-semibold" disabled={isProcessingRectify || !rectifyLoanForm.originalTxnId}>
                      {isProcessingRectify ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Rectifying...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-check-circle me-2"></i>
                          Rectify Payment
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </Portal>

      {showApproveModal && selectedPenalty && (
        <div className="modal fade show d-block" tabIndex={-1} role="dialog" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content border-0 shadow-lg rounded-3">
              <div className="modal-header border-0 pb-0">
                <div>
                  <h5 className="modal-title fw-bold">Approve Charge</h5>
                  <p className="text-muted small mb-0">Confirm approval for this penalty review</p>
                </div>
                <button type="button" className="btn-close" onClick={() => setShowApproveModal(false)} aria-label="Close"></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-success py-2 mb-3">
                  This will approve the penalty and apply the charge.
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Due Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={approvalDueDate}
                    onChange={(e) => setApprovalDueDate(e.target.value)}
                  />
                  <small className="text-muted d-block mt-2">
                    This uses the pending charge’s due date by default. You can keep it or change it before approving.
                  </small>
                </div>
              </div>
              <div className="modal-footer border-0">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowApproveModal(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleApprove}
                  disabled={!selectedPenalty.id || processingPenaltyAction === 'approve'}
                >
                  {processingPenaltyAction === 'approve' ? 'Approving...' : 'Approve'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && selectedPenalty && (
        <div className="modal fade show d-block" tabIndex={-1} role="dialog" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content border-0 shadow-lg rounded-3">
              <div className="modal-header border-0 pb-0">
                <div>
                  <h5 className="modal-title fw-bold">Reject Charge</h5>
                  <p className="text-muted small mb-0">Provide a reason for rejecting this penalty review</p>
                </div>
                <button type="button" className="btn-close" onClick={() => setShowRejectModal(false)} aria-label="Close"></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-danger py-2 mb-3">
                  This will reject the penalty review and no charge will be applied.
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Reason</label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Enter the rejection reason"
                  />
                </div>
              </div>
              <div className="modal-footer border-0">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowRejectModal(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleReject}
                  disabled={!rejectionReason.trim() || processingPenaltyAction === 'reject'}
                >
                  {processingPenaltyAction === 'reject' ? 'Rejecting...' : 'Reject'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Override Modal */}
      <div className="modal fade modal-slide-in-left" id="DueCollectionOverride" tabIndex={-1} aria-labelledby="DueCollectionOverrideLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: '700px', width: '92%', margin: '1rem auto' }}>
          <div className="modal-content rounded-0 border-0 shadow-lg" style={{ display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div className="modal-header border-bottom pb-0" style={{ padding: '2rem 2rem 1.5rem 2rem', backgroundColor: '#f8f9fa' }}>
              <div className="w-100">
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle p-3 me-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(13,202,240,0.2)' }}>
                      <i className="fa fa-edit fa-2x text-info" style={{ display: 'block' }}></i>
                    </div>
                    <div>
                      <h4 className="modal-title mb-0 text-dark fw-bold" id="DueCollectionOverrideLabel">
                Override Charge Amount
                      </h4>
                      <p className="text-muted mb-0 mt-1 small">Override the calculated charge amount for this account</p>
                    </div>
                  </div>
                </div>
              </div>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>

            <div className="modal-body p-4" style={{ backgroundColor: '#f8f9fa' }}>
              <div className="row g-3 mb-4">
                <div className="col-6">
                  <div className="rounded-3 p-3 border bg-white" style={{ borderColor: '#e9ecef', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle p-2 me-2" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
                        <i className="fa fa-calendar text-primary" style={{ display: 'block' }}></i>
                      </div>
                      <div className="flex-grow-1">
                        <div className="text-muted small">As Of Date</div>
                        <div className="text-dark fw-semibold small">{selectedPenalty ? formatDate(selectedPenalty.calculated?.asOfDate || '') : '-'}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-6">
                  <div className="rounded-3 p-3 border bg-white" style={{ borderColor: '#e9ecef', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle p-2 me-2" style={{ backgroundColor: '#fff3cd', border: '1px solid rgba(255,193,7,0.2)' }}>
                        <i className="fa fa-exclamation-circle text-warning" style={{ display: 'block' }}></i>
                      </div>
                      <div className="flex-grow-1">
                        <div className="text-muted small">Current Amount</div>
                        <div className="text-warning fw-bold">{formatCurrency(selectedPenalty?.calculated?.amountDec || 0)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Override Amount Section */}
              {/* Override Amount Section */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center mb-4">
                    <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(13,202,240,0.2)' }}>
                      <i className="fa fa-money text-info" style={{ display: 'block' }}></i>
                    </div>
                    <div>
                      <h6 className="mb-0 fw-bold">Override Amount</h6>
                      <small className="text-muted">Enter the new charge amount</small>
                    </div>
                  </div>

                  <div className="row g-4">
                      <div className="col-md-6">
                      <label htmlFor="overrideAsOfDate" className="form-label fw-semibold mb-2">
                            As Of Date <span className="text-danger">*</span>
                          </label>
                      <div className="input-group input-group-lg" style={{ borderRadius: '0.5rem', overflow: 'hidden', border: !overrideAsOfDate ? '2px solid #dc3545' : '1px solid #dee2e6' }}>
                        <span className="input-group-text bg-white border-0 px-4">
                          <i className="fa fa-calendar text-info fa-lg" style={{ display: 'block' }}></i>
                        </span>
                        <input
                          type="date"
                          id="overrideAsOfDate"
                          className={`form-control border-0 ${!overrideAsOfDate ? 'is-invalid' : ''}`}
                          style={{ fontSize: '1.1rem', fontWeight: 500 }}
                          value={overrideAsOfDate}
                          onChange={(e) => setOverrideAsOfDate(e.target.value)}
                          min="1900-01-01"
                        />
                      </div>
                      {!overrideAsOfDate && (
                        <div className="text-danger small mt-2">
                          <i className="fa fa-exclamation-circle me-1"></i>
                          As Of Date is required
                        </div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="overrideAmount" className="form-label fw-semibold mb-2">
                            New Amount <span className="text-danger">*</span>
                          </label>
                      <div className="input-group input-group-lg" style={{ borderRadius: '0.5rem', overflow: 'hidden', border: !overrideAmount ? '2px solid #dc3545' : '1px solid #dee2e6' }}>
                        <span className="input-group-text bg-white border-0 px-4">
                          <i className="fa fa-rand text-info fa-lg" style={{ display: 'block' }}></i>
                        </span>
                        <input
                          type="number"
                          id="overrideAmount"
                          className={`form-control border-0 ${!overrideAmount ? 'is-invalid' : ''}`}
                          placeholder="0.00"
                          style={{ fontSize: '1.1rem', fontWeight: 500 }}
                          value={overrideAmount}
                          onChange={(e) => setOverrideAmount(Number(e.target.value))}
                          min="0.01"
                          step="0.01"
                        />
                      </div>
                      {!overrideAmount && (
                        <div className="text-danger small mt-2">
                          <i className="fa fa-exclamation-circle me-1"></i>
                          New amount is required
                        </div>
                      )}
                      {selectedPenalty?.calculated?.amountDec && (
                        <div className="mt-2 p-2 rounded" style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107' }}>
                          <small className="text-warning d-flex align-items-center">
                            <i className="fa fa-info-circle me-2"></i>
                            <span>Current charge amount: <strong>{formatCurrency(selectedPenalty.calculated.amountDec)}</strong></span>
                          </small>
                        </div>
                      )}
                    </div>
                      </div>
                    </div>
                  </div>

              {/* Due Date Section */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center mb-4">
                    <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#d4edda', border: '1px solid rgba(40,167,69,0.2)' }}>
                      <i className="fa fa-calendar-check-o text-success" style={{ display: 'block' }}></i>
                    </div>
                    <div>
                      <h6 className="mb-0 fw-bold">New Due Date (Optional)</h6>
                      <small className="text-muted">Optionally set a new due date when overriding the charge</small>
                    </div>
                  </div>

                  <div className="row g-4">
                    <div className="col-md-6">
                      <label htmlFor="overrideDueDate" className="form-label fw-semibold mb-2">
                        New Due Date
                      </label>
                      <div className="input-group input-group-lg" style={{ borderRadius: '0.5rem', overflow: 'hidden', border: '1px solid #dee2e6' }}>
                        <span className="input-group-text bg-white border-0 px-4">
                          <i className="fa fa-calendar text-success fa-lg" style={{ display: 'block' }}></i>
                        </span>
                        <input
                          type="date"
                          id="overrideDueDate"
                          className="form-control border-0"
                          style={{ fontSize: '1.1rem', fontWeight: 500 }}
                          value={overrideDueDate}
                          onChange={(e) => setOverrideDueDate(e.target.value)}
                        />
                      </div>
                      <small className="text-muted d-block mt-2">
                        <i className="fa fa-lightbulb-o me-1"></i>
                        Optional: select if the installment's due date should change with this override
                      </small>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold mb-2">
                        Current Due Date
                      </label>
                      <div className="input-group input-group-lg" style={{ borderRadius: '0.5rem', overflow: 'hidden', border: '1px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
                        <span className="input-group-text bg-white border-0 px-4" style={{ backgroundColor: '#f8f9fa' }}>
                          <i className="fa fa-calendar text-secondary fa-lg" style={{ display: 'block' }}></i>
                        </span>
                        <input
                          type="text"
                          className="form-control border-0 bg-light"
                          style={{ fontSize: '1.1rem', fontWeight: 500 }}
                          value={getCurrentDueDate(loanAccount)}
                          readOnly
                          disabled
                        />
                      </div>
                      <small className="text-muted d-block mt-2">
                        <i className="fa fa-info-circle me-1"></i>
                        Current installment due date for reference
                      </small>
                    </div>
                  </div>

                                    {overrideDueDate && (
                    <div className="alert alert-warning border-0 mt-3 mb-0" style={{ backgroundColor: '#fff3cd', borderLeft: '4px solid #ffc107' }}>
                      <div className="d-flex align-items-start">
                        <i className="fa fa-exclamation-triangle text-warning me-2 mt-1"></i>
                        <div>
                          <strong className="d-block mb-1">Important Notice</strong>
                          <small className="d-block">
                            Changing the due date while overriding a charge will update the associated repayment schedule. Ensure this is aligned with your operational policy and has been communicated to the customer.
                          </small>
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              </div>
            </div>
            <div className="modal-footer border-top bg-white py-3 px-4 d-flex justify-content-end gap-3">
              <button type="button" className="btn btn-outline-secondary px-5 py-2 rounded-pill" data-bs-dismiss="modal">
                <i className="fa fa-times me-2"></i>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-info px-5 py-2 rounded-pill shadow-sm fw-semibold"
                onClick={handleOverride}
                disabled={!overrideAmount || !overrideAsOfDate || processingPenaltyAction === 'override'}
              >
                {processingPenaltyAction === 'override' ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                ) : (
                  <i className="fa fa-check-circle me-2"></i>
                )}
                Confirm Override
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Calculate Charges Modal */}

      {/* View Remarks Modal */}
      {showViewRemarksModal && viewRemarksTarget && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '12px' }}>
              <div className="modal-header border-bottom" style={{ padding: '1.5rem 2rem', backgroundColor: '#f8f9fa' }}>
                <div className="d-flex align-items-center">
                  <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#e7f3ff', border: '1px solid rgba(13,110,253,0.2)' }}>
                    <i className="fa fa-comment text-primary" style={{ display: 'block' }}></i>
                  </div>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold">Payment Remarks</h5>
                    <p className="text-muted mb-0 small">
                      {viewRemarksTarget.valueDate ? formatDate(viewRemarksTarget.valueDate) : '—'} • {formatCurrency(Math.abs(viewRemarksTarget.amountDec || 0))}
                    </p>
                  </div>
                </div>
                <button type="button" className="btn-close" onClick={() => setShowViewRemarksModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                <div className="row g-3">
                  {viewRemarksTarget.remarks ? (
                    <div className="col-12">
                      <small className="text-muted d-block mb-1 fw-semibold">Remarks</small>
                      <div className="p-3 bg-light rounded" style={{ whiteSpace: 'pre-wrap' }}>
                        {viewRemarksTarget.remarks}
                      </div>
                    </div>
                  ) : (
                    <div className="col-12 text-center py-3">
                      <i className="fa fa-comment-slash fa-2x text-muted mb-2" style={{ display: 'block' }}></i>
                      <p className="text-muted mb-0">No remarks for this payment.</p>
                    </div>
                  )}
                  {viewRemarksTarget.externalRef && (
                    <div className="col-12">
                      <small className="text-muted d-block mb-1 fw-semibold">Bank Reference</small>
                      <div className="p-3 bg-light rounded">
                        <i className="fa fa-tag me-2 text-secondary"></i>
                        {viewRemarksTarget.externalRef}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer border-0 px-4 pb-4">
                <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowViewRemarksModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Calculate Charges Modal */}
      {showChargesModal && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '12px' }}>
              <div className="modal-header border-bottom" style={{ padding: '1.5rem 2rem', backgroundColor: '#f8f9fa' }}>
                <div className="d-flex align-items-center">
                  <div className="rounded-circle p-3 me-3" style={{ backgroundColor: '#fff3cd', border: '1px solid rgba(255,193,7,0.3)' }}>
                    <i className="fa fa-calculator fa-lg text-warning" style={{ display: 'block' }}></i>
                  </div>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold">
                      {!calculationResult ? 'Calculate Charges' : 'Calculation Result'}
                    </h5>
                    <p className="text-muted mb-0 small">
                      {!calculationResult ? 'Configure charge calculation for this account' : 'Charge calculation has been completed'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowChargesModal(false);
                    setCalculationResult(null);
                  }}
                ></button>
              </div>
              <div className="modal-body p-4">
                {!calculationResult ? (
                  // Form view
                  <>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">As of Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={chargesAsOfDate}
                        onChange={(e) => setChargesAsOfDate(e.target.value)}
                        disabled={isCalculating}
                      />
                      <small className="text-muted">Select the date to calculate charges up to</small>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">New Due Date (Optional)</label>
                      <input
                        type="date"
                        className="form-control"
                        value={chargesNewDueDate}
                        onChange={(e) => setChargesNewDueDate(e.target.value)}
                        disabled={isCalculating}
                      />
                      <small className="text-muted">Set a new repayment due date for this account (leave empty to keep current)</small>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">Calculation Method</label>
                      <select
                        className="form-select"
                        value={chargesStrategy}
                        onChange={(e) => setChargesStrategy(e.target.value as 'DEFAULT' | 'OVERRIDE_RATE')}
                        disabled={isCalculating}
                      >
                        <option value="DEFAULT">Default (system rate)</option>
                        <option value="OVERRIDE_RATE">Manual rate override</option>
                      </select>
                    </div>

                    {chargesStrategy === 'DEFAULT' && (
                      <div className="mb-3 p-3 rounded" style={{ backgroundColor: '#e7f3ff', border: '1px solid #b3d9ff' }}>
                        <div className="d-flex align-items-start">
                          <i className="fa fa-info-circle text-primary me-2 mt-1"></i>
                          <div className="small text-dark">
                            <span>The system will apply the rate to the correct base amount based on the product rules. Press <strong>Calculate</strong> to determine the exact charge.</span>
                            <div className="mt-2 d-flex gap-3">
                              <div>
                                <span className="text-muted">Principal:</span>{' '}
                                <strong>{formatCurrency(loanAccount?.disbursedAmount || 0)}</strong>
                              </div>
                              <div>
                                <span className="text-muted">Outstanding:</span>{' '}
                                <strong>{formatCurrency(loanAccount?.totalOutstanding || 0)}</strong>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {chargesStrategy === 'OVERRIDE_RATE' && (
                      <div className="mb-3">
                        <label className="form-label fw-semibold">Override Rate (%)</label>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="Enter rate percentage"
                          value={chargesManualAmount}
                          onChange={(e) => setChargesManualAmount(e.target.value)}
                          min="0"
                          step="0.01"
                          disabled={isCalculating}
                        />
                        <small className="text-muted">Enter the penalty rate percentage to apply</small>
                        {chargesManualAmount && Number(chargesManualAmount) > 0 && loanAccount?.totalOutstanding && (
                          <div className="mt-2 p-3 rounded" style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107' }}>
                            <div className="d-flex align-items-center justify-content-between">
                              <span className="text-dark small fw-semibold">Estimated charge amount:</span>
                              <span className="fw-bold text-warning-emphasis">
                                {formatCurrency((Number(chargesManualAmount) / 100) * loanAccount.totalOutstanding)}
                              </span>
                            </div>
                            <small className="text-muted d-block mt-1">
                              {chargesManualAmount}% of {formatCurrency(loanAccount.totalOutstanding)} outstanding
                            </small>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  // Result view
                  <div className="text-center">
                    {calculationResult.amountDec > 0 ? (
                      <>
                        <div className="mb-4">
                          <i className="fa fa-calculator fa-3x text-success mb-3"></i>
                          <h5 className="fw-bold text-success">Calculation Complete</h5>
                          <p className="text-muted">Charges have been calculated and saved as pending.</p>
                        </div>

                        <div className="mb-4 p-4 rounded" style={{ backgroundColor: '#e7f3ff', border: '1px solid #b3d9ff' }}>
                          <div className="d-flex align-items-center justify-content-center mb-3">
                            <i className="fa fa-info-circle text-primary me-2"></i>
                            <span className="text-dark">
                              System rate will be applied on <strong>{formatCurrency(calculationResult.baseAmount || 0)}</strong> {getBaseAmountLabel(calculationResult.baseAmountType)}.
                            </span>
                          </div>
                          <div className="row g-3">
                            <div className="col-6">
                              <div className="p-3 bg-white rounded border">
                                <small className="text-muted d-block">Calculated Amount</small>
                                <div className="fw-bold text-danger">{formatCurrency(calculationResult.amountDec || 0)}</div>
                              </div>
                            </div>
                            <div className="col-6">
                              <div className="p-3 bg-white rounded border">
                                <small className="text-muted d-block">Rate Applied</small>
                                <div className="fw-bold text-info">{calculationResult.ruleRatePercent || 0}%</div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="alert alert-warning">
                          <i className="fa fa-exclamation-triangle me-2"></i>
                          This calculation has been saved as a pending charge. You can review and approve it in the Pending Charges section.
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="mb-4">
                          <i className="fa fa-check-circle fa-3x text-info mb-3"></i>
                          <h5 className="fw-bold text-info">No Charges to Apply</h5>
                          <p className="text-muted">The account is not overdue, so no charges were calculated.</p>
                        </div>
                        <div className="alert alert-info">
                          <i className="fa fa-info-circle me-2"></i>
                          No action is required.
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer border-0 px-4 pb-4">
                {!calculationResult ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-outline-secondary rounded-pill px-4"
                      onClick={() => setShowChargesModal(false)}
                      disabled={isCalculating}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-warning rounded-pill px-4 fw-semibold"
                      onClick={handleCalculatePenalties}
                      disabled={!chargesAsOfDate || (chargesStrategy === 'OVERRIDE_RATE' && !chargesManualAmount) || isCalculating}
                    >
                      {isCalculating ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Calculating...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-calculator me-2"></i>
                          Calculate
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn btn-success rounded-pill px-4"
                    onClick={() => {
                      setShowChargesModal(false);
                      setCalculationResult(null);
                      setChargesAsOfDate(toLocalDateString());
                      setChargesNewDueDate('');
                      setChargesManualAmount('');
                      setChargesStrategy('DEFAULT');
                    }}
                  >
                    <i className="fa fa-check me-2"></i>
                    Close
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Audit Trail Modal */}
      {showAuditTrailModal && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '12px' }}>
              <div className="modal-header border-bottom" style={{ padding: '1.5rem 2rem', backgroundColor: '#f8f9fa' }}>
                <div className="d-flex align-items-center">
                  <div className="rounded-circle p-3 me-3" style={{ backgroundColor: '#e2e3e5', border: '1px solid rgba(108,117,125,0.3)' }}>
                    <i className="fa fa-list-alt fa-lg text-secondary" style={{ display: 'block' }}></i>
                  </div>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold">Audit Trail</h5>
                    <p className="text-muted mb-0 small">
                      Full transaction history for {loanAccount?.customerName || 'this account'}
                      {allTransactions.length > 0 && ` — ${allTransactions.length} entries`}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowAuditTrailModal(false)}
                ></button>
              </div>
              <div className="modal-body p-4" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                {allTransactions.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="fa fa-inbox fa-3x text-muted mb-3" style={{ display: 'block' }}></i>
                    <h6 className="text-muted">No Transactions</h6>
                    <p className="text-muted mb-0 small">No transaction history available.</p>
                  </div>
                ) : (
                  <div className="position-relative" style={{ paddingLeft: '2rem' }}>
                    <div className="position-absolute" style={{ left: '0.75rem', top: 0, bottom: 0, width: '2px', backgroundColor: '#dee2e6' }}></div>
                    {allTransactions.map((txn) => {
                      const isReversal = txn.txnType === 'REVERSAL';
                      const isRectified = txn.txnType === 'RECTIFIED';
                      const wasRectified = rectifiedOriginalTxnIds.includes(txn.txnId);

                      let iconClass = 'fa-money';
                      let bgColor = '#d4edda';
                      let borderColor = 'rgba(40,167,69,0.3)';
                      let textColor = 'text-success';
                      // Label by what the entry actually is (it lists every ledger entry, not only payments)
                      const typeLabels: Record<string, string> = {
                        PAYMENT: 'Payment', DRAWDOWN: 'Disbursement', CHARGE: 'Charge', DISCOUNT: 'Discount',
                        WRITE_OFF: 'Write-off', TRANSFER: 'Transfer',
                      };
                      let label = typeLabels[txn.txnType || ''] || txn.txnType || 'Entry';
                      if (txn.txnType === 'DRAWDOWN' || txn.txnType === 'CHARGE') {
                        iconClass = txn.txnType === 'DRAWDOWN' ? 'fa-arrow-up' : 'fa-plus-circle';
                        bgColor = '#e7f1ff';
                        borderColor = 'rgba(13,110,253,0.3)';
                        textColor = 'text-primary';
                      } else if (txn.txnType === 'DISCOUNT') {
                        iconClass = 'fa-tag';
                      }

                      if (isReversal) {
                        iconClass = 'fa-undo';
                        bgColor = '#f8d7da';
                        borderColor = 'rgba(220,53,69,0.3)';
                        textColor = 'text-danger';
                        label = 'Reversal';
                      } else if (isRectified) {
                        iconClass = 'fa-exchange';
                        bgColor = '#fff3cd';
                        borderColor = 'rgba(255,193,7,0.3)';
                        textColor = 'text-warning';
                        label = 'Rectified';
                      } else if (wasRectified) {
                        iconClass = 'fa-times-circle';
                        bgColor = '#e2e3e5';
                        borderColor = 'rgba(108,117,125,0.3)';
                        textColor = 'text-secondary';
                        label = 'Original (Rectified)';
                      }

                      return (
                        <div key={txn.txnId} className="d-flex mb-3">
                          <div className="position-relative" style={{ marginLeft: '-1.45rem', marginRight: '1rem' }}>
                            <div
                              className="rounded-circle d-flex align-items-center justify-content-center"
                              style={{ width: '28px', height: '28px', backgroundColor: bgColor, border: `2px solid ${borderColor}`, zIndex: 1, position: 'relative' }}
                            >
                              <i className={`fa ${iconClass} ${textColor}`} style={{ fontSize: '0.7rem' }}></i>
                            </div>
                          </div>
                          <div className={`flex-grow-1 p-3 rounded border ${wasRectified ? 'bg-light' : 'bg-white'}`} style={{ borderColor: '#e9ecef' }}>
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <span className={`badge ${isReversal ? 'bg-danger-subtle text-danger-emphasis' : isRectified ? 'bg-warning-subtle text-warning-emphasis' : wasRectified ? 'bg-secondary-subtle text-secondary-emphasis' : 'bg-success-subtle text-success-emphasis'} me-2`}>
                                  {label}
                                </span>
                                <span className={`fw-bold ${wasRectified ? 'text-decoration-line-through text-muted' : textColor}`}>
                                  {formatCurrency(Math.abs(txn.amountDec || 0))}
                                </span>
                              </div>
                              <small className="text-muted">
                                {txn.valueDate ? formatDate(txn.valueDate) : '—'}
                              </small>
                            </div>
                            <div className="mt-1">
                              {txn.externalRef && (
                                <small className="text-muted d-block">
                                  <i className="fa fa-tag me-1"></i>Ref: {txn.externalRef}
                                </small>
                              )}
                              {txn.remarks && (
                                <small className="text-muted d-block">
                                  <i className="fa fa-comment me-1"></i>{txn.remarks}
                                </small>
                              )}
                              {txn.reversedTxnId && (
                                <small className="text-muted d-block">
                                  <i className="fa fa-link me-1"></i>Linked to: {txn.reversedTxnId.substring(0, 8)}...
                                </small>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="modal-footer border-0 px-4 pb-4">
                <button
                  type="button"
                  className="btn btn-outline-secondary rounded-pill px-4"
                  onClick={() => setShowAuditTrailModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoanAccountDetails;
